// ChatBubble — 右下浮動小球 + 展開後的對話視窗。
//
// Spec: docs/specs/ai-chat-guided-tour.md §6.1 (chat 視窗) + §6.2 ↔ §9 (SSE).
//
// Phase 1 (PR #3) 範圍：
//   - 浮動小球 (右下 24/24px, 桌機 400×min(80vh,720) / 手機 90vw×75vh anchor)
//   - thread CRUD + 訊息送出 + SSE echo 串流顯示
//   - feature flag gate (/v1/ai/availability)
//   - 「需要協助設定嗎？」首登 tooltip (OQ C3)
//   - 二次登入預設收起 (OQ C4)
//   - 清晰錯誤 + quota 顯示
//
// 後續 PR 會把：
//   - PR #4 token streaming 接上真實 OpenAI Responses API（介面不變）
//   - PR #5 加 tool_call 視覺化（assistant 在跑 list_sites 時的 status 列）
//   - PR #6 加 propose_tour 確認 modal + 引導引擎啟動
//   - PR #7 加 memory settings drawer + 清除按鈕

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, X, Plus, Send, RefreshCw, Trash2, AlertCircle, ThumbsUp, ThumbsDown,
  ArrowRight, Settings, User, Shield, FileText, Info, ChevronDown, Archive,
} from "lucide-react";
import { ROUTE_PERMISSIONS } from "../lib/nav.js";
import {
  getAiAvailability,
  listAiThreads,
  createAiThread,
  getAiThread,
  deleteAiThread,
  sendAiMessage,
  sendAiToolResults,
  sendAiFeedback,
  cancelAiTurn,
  startTourRun,
  logTourStep,
  completeTourRun,
  getAiPreferences,
  setAiPreference,
  friendlyChatError,
  type AiThreadRow,
  type AiMessageRow,
  type AiAvailability,
  type SendMessageEvents,
  type SseFinal,
  type SseToolCall,
  type AiTourProposal,
  type AiTourStep,
  type QuickNavItem,
  type QuickNavProposal,
  type PlanProposal,
  type QuestionsProposal,
  type QuestionsAnswerMap,
  type ToolResultPayload,
} from "../lib/chatApi.js";
import { executeAiTool } from "./tool-executor.js";
import PropQuestionsCard from "./PropQuestionsCard.js";
import { useScreenMeta } from "./ScreenMetaContext.js";
import { TourConfirmModal, TourEngine } from "./TourEngine.js";
import PlanModal from "./PlanModal.js";
import AiConsoleIconRow from "./AiConsoleIconRow.js";
import { useAiConsoleSlot } from "./AiConsoleSlotContext.js";
import ChatMarkdown from "./ChatMarkdown.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useAiMode } from "../AiModeContext.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";
import { BindingCodeIssuedCard } from "../components/BindingCodeIssueModal.js";

// localStorage key for "should I show the first-login tooltip?". Per
// spec §6.1: shows on first login + when tenant is empty; suppressed
// for 30 days after user closes.
const TOOLTIP_DISMISSED_AT = "ai_chat.tooltip_dismissed_at_v1";

/** Bubble pixel measurements — keep in sync with the className below
 *  (`h-12 w-12 bottom-6 right-6`). 24 px gap = `right-6` / `bottom-6`,
 *  48 px diameter = `h-12 w-12`. After drag the bubble snaps to whichever
 *  vertical edge (left / right) is closer; the gap stays at EDGE_GAP_PX. */
const BUBBLE_DIAMETER_PX = 48;
const EDGE_GAP_PX = 24;
/** Pixel threshold below which a pointerdown→pointerup is treated as a
 *  click rather than a drag. Calibrated for finger taps on mobile —
 *  touches sometimes register 2-3 px of accidental movement even on a
 *  pure tap. */
const DRAG_THRESHOLD_PX = 6;
/** localStorage key for the bubble's last position. Value:
 *    { y: number /* px from top *\/, side: "left" | "right" }
 *  v1 → we tied position to viewport size which broke on resize.
 *  v2 stores the snap side + a 0-1 ratio of viewport height so the
 *  bubble lands at the same relative spot across screen sizes. */
const BUBBLE_POS_STORAGE = "ai_chat.bubble_pos_v2";

interface BubblePos { side: "left" | "right"; yRatio: number }

function loadBubblePos(): BubblePos {
  try {
    const raw = localStorage.getItem(BUBBLE_POS_STORAGE);
    if (!raw) return { side: "right", yRatio: defaultYRatio() };
    const parsed = JSON.parse(raw) as Partial<BubblePos>;
    const side = parsed.side === "left" ? "left" : "right";
    const yRatio = typeof parsed.yRatio === "number" && parsed.yRatio >= 0 && parsed.yRatio <= 1
      ? parsed.yRatio
      : defaultYRatio();
    return { side, yRatio };
  } catch {
    return { side: "right", yRatio: defaultYRatio() };
  }
}

function saveBubblePos(p: BubblePos): void {
  try { localStorage.setItem(BUBBLE_POS_STORAGE, JSON.stringify(p)); } catch { /* private mode */ }
}

/** Default Y as a ratio — matches old `bottom-6` (24 px from bottom). */
function defaultYRatio(): number {
  if (typeof window === "undefined") return 0.9;
  const h = window.innerHeight || 800;
  const yFromTop = h - EDGE_GAP_PX - BUBBLE_DIAMETER_PX;
  return Math.max(0, Math.min(1, yFromTop / h));
}
// "Chat bubble open at last unmount?" → not persisted by design;
// spec §6.1 OQ C4: 二次登入 chat 視窗預設收起。

type Phase =
  | { kind: "loading_availability" }
  | { kind: "unavailable"; reason: string | null }
  | { kind: "ready"; availability: AiAvailability };

type ThreadsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; items: AiThreadRow[] }
  | { kind: "error"; message: string };

type DetailState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; thread: AiThreadRow; messages: AiMessageRow[] }
  | { kind: "error"; message: string };

/** Local-only bubble appended to the messages list after the operator
 *  completes a credential-bearing drawer-form. Doesn't persist to the
 *  user_ai_messages DB and never gets shipped to OpenAI on the next
 *  turn — the only place it lives is `localCards` state in ChatBubble.
 *  See `handleDrawerFormComplete` for the issue_binding_code path. */
type LocalCard =
  | { kind: "issued_binding_code"; thread_id: string; created_at: string; issued: import("../components/BindingCodeIssueModal.js").IssuedCode };

interface StreamingAssistant {
  /** Temporary in-flight assistant message. Replaced by the real DB
   *  row on `final` event, or removed on `error`. */
  text: string;
  user_message_id: string;     // local placeholder (replaced when server confirms)
  startedAt: string;
  /** Quick-nav buttons live-emitted via the `quick_nav` SSE event.
   *  Rendered immediately on the streaming bubble so the user can tap
   *  them while AI is still finishing its prose. After the `final`
   *  event lands, the same items are also embedded in tool_calls JSON
   *  on the persisted message row — MessageBubble derives them from
   *  there for chat-history re-renders. */
  quick_nav?: QuickNavItem[] | null;
  /** Multi-question picker stacks live-emitted via the
   *  `propose_questions` SSE event. Each emission is one stack of
   *  1-4 questions submitted together. Persisted on the assistant
   *  message via tool_calls JSON post-stream so chat history shows
   *  them too. */
  questionStacks?: QuestionsProposal[] | null;
}

/** Props for ChatBubble.
 *
 *  - `variant: "floating"` (default): the original floating bubble UX
 *    — round button bottom-right, click to expand, drag to reposition,
 *    snap to edge.
 *  - `variant: "inline"`: the chat window is rendered AS-IS inside the
 *    parent layout (no bubble button, no drag, no fixed positioning).
 *    Used by the AI 導覽 page (Topology in AI mode) which dedicates a
 *    portion of the page to the chat panel directly. Spec
 *    docs/specs/ai-mode-operations-console.md §4.5.
 *
 *  `onPlanApplyComplete` fires AFTER the operator closes the
 *  PlanModal — gives the host page a chance to react to apply results
 *  (e.g. refetch topology + URL-navigate with newly-created IDs in
 *  ?expanded= / ?highlight=). Receives the raw OpResult[] array. */
/** AM-5b plan-host plumbing. When `planHost === "external"` the host
 *  page (AI 導覽 Topology) takes over plan rendering — ChatBubble
 *  doesn't render its own floating PlanModal but still owns the
 *  activePlan state machine + receives propose_plan SSE events.
 *  Whenever activePlan transitions (set / cleared) `onActivePlanChange`
 *  fires with everything the host needs to render PlanPanel inside its
 *  own surface (drawer): the proposal, the thread id, and the same
 *  internal close handler the floating modal would have used (so the
 *  AI thread still gets the tool_result on dismiss).
 *
 *  When `planHost === "modal"` (default) ChatBubble keeps current
 *  behaviour — own PlanModal, no callback. */
export interface ActivePlanInfo {
  plan: PlanProposal;
  threadId: string;
  tenantId: string | null;
  onClose: (results: import("./PlanModal.js").OpResult[]) => void;
}

/** AM-7 — mirrors ActivePlanInfo for drawer create-forms. Host (AI 導覽
 *  Topology) renders DrawerFormHost using `kind` + `prefill` + `reason`
 *  to pick the right form; on save / cancel the host calls `onComplete`
 *  which posts the tool_result back to the AI thread via ChatBubble's
 *  internal `handleDrawerFormComplete` (so the AI gets the same shape
 *  of result regardless of where the form was hosted). */
export interface ActiveDrawerFormInfo {
  kind: import("../lib/chatApi.js").DrawerFormKind;
  callId: string;
  threadId: string;
  tenantId: string | null;
  prefill: Record<string, unknown>;
  reason: string;
  onComplete: (outcome: import("./DrawerFormHost.js").DrawerFormOutcome) => void;
}

export interface ChatBubbleProps {
  variant?: "floating" | "inline";
  onPlanApplyComplete?: (results: import("./PlanModal.js").OpResult[]) => void;
  /** Where Plan & Apply renders. Defaults to "modal" (floating
   *  overlay). "external" delegates rendering to the host via
   *  `onActivePlanChange`. */
  planHost?: "modal" | "external";
  /** Fires whenever the internal activePlan state transitions. Host
   *  is expected to render PlanPanel using the supplied close handler
   *  when info is non-null; clear its slot when info is null. Only
   *  relevant when `planHost === "external"`. */
  onActivePlanChange?: (info: ActivePlanInfo | null) => void;
  /** AM-7 — fires whenever AI emits propose_drawer_form. Host renders
   *  the form via DrawerFormHost inside the AI 導覽 drawer; on
   *  completion calls `info.onComplete` which routes back through
   *  ChatBubble to post the tool_result + clear state. Only meaningful
   *  in inline variant; floating mode silently drops the proposal
   *  (drawer doesn't exist outside AI 導覽). */
  onActiveDrawerFormChange?: (info: ActiveDrawerFormInfo | null) => void;
  /** AM-7 — fires after the drawer form completes so the host can react
   *  (auto-switch drawer to topology, refetch + highlight new node).
   *  Fires for both saved + cancelled outcomes; host inspects kind. */
  onDrawerFormComplete?: (outcome: import("./DrawerFormHost.js").DrawerFormOutcome) => void;
}

export default function ChatBubble({
  variant = "floating",
  onPlanApplyComplete,
  planHost = "modal",
  onActivePlanChange,
  onActiveDrawerFormChange,
  onDrawerFormComplete,
}: ChatBubbleProps = {}): React.ReactElement | null {
  const session = useDashboardSession();
  const [phase, setPhase] = useState<Phase>({ kind: "loading_availability" });
  // Inline variant: the panel is always "expanded" because there's no
  // floating button to toggle it from. We still keep the state hook so
  // the rest of the component logic (which reads `expanded`) keeps
  // working uniformly across variants.
  const [expanded, setExpanded] = useState(variant === "inline");
  const [showTooltip, setShowTooltip] = useState(false);
  /** Bubble position state. `bubblePos` is the committed snap target;
   *  `dragOffset`, if non-null, is the live pixel offset during a drag
   *  (relative to viewport, set on every pointermove). When dragging
   *  ends we recompute `bubblePos` from `dragOffset` (snap to nearest
   *  vertical edge) and clear dragOffset to let the snap transition
   *  animate. */
  const [bubblePos, setBubblePos] = useState<BubblePos>(() => loadBubblePos());
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  /** True once pointer has moved past DRAG_THRESHOLD_PX — used to
   *  suppress the click-to-expand on pointerup when the gesture was
   *  actually a drag. Reset to false on every pointerdown. */
  const dragMovedRef = useRef(false);
  const dragStartRef = useRef<{ pointerId: number; offsetX: number; offsetY: number } | null>(null);

  // Persist position changes (snap commits) to localStorage so the
  // bubble stays put across page reloads. Pure derived effect; no API.
  useEffect(() => { saveBubblePos(bubblePos); }, [bubblePos]);

  /** Chat panel animation phase. `chatOpening` is set transiently when
   *  the bubble is clicked → triggers `animate-chat-pop-in` on the
   *  panel. `chatClosing` is set when the operator clicks an inline
   *  quick-nav button on MOBILE → triggers `animate-chat-slide-out`
   *  then setExpanded(false) after the keyframe completes.
   *
   *  Desktop doesn't animate close (chat is a 400×720 corner widget;
   *  the destination page is already visible behind it). Mobile DOES
   *  animate close because the chat takes ~95% of the viewport and
   *  cutting it instantly would feel jarring.
   *
   *  Both flags clear themselves via the useEffect timers below. */
  const [chatOpening, setChatOpening] = useState(false);
  const [chatClosing, setChatClosing] = useState(false);

  // Clear `opening` after the pop-in keyframe finishes (220ms keyframe
  // + small buffer). We can't rely on onAnimationEnd alone because the
  // panel uses `display: none ↔ block` which sometimes skips the event
  // — easier to time it.
  useEffect(() => {
    if (!chatOpening) return;
    const t = setTimeout(() => setChatOpening(false), 240);
    return () => clearTimeout(t);
  }, [chatOpening]);

  // After slide-out finishes, actually unmount-via-display-none + clear
  // closing flag so the next open starts clean.
  useEffect(() => {
    if (!chatClosing) return;
    const t = setTimeout(() => {
      setExpanded(false);
      setChatClosing(false);
    }, 200);
    return () => clearTimeout(t);
  }, [chatClosing]);

  // Listen for inline-button close requests from QuickNavRow (custom
  // event on window — avoids deep prop drilling through ChatWindow →
  // MessageList → MessageBubble → QuickNavRow). The button handler
  // dispatches the event ONLY on mobile viewports; desktop just
  // navigates and leaves the chat open.
  useEffect(() => {
    const handler = (): void => {
      if (!expanded) return;
      setChatClosing(true);
    };
    window.addEventListener("ai-chat:close-animated", handler);
    return () => window.removeEventListener("ai-chat:close-animated", handler);
  }, [expanded]);

  // Mount once: feature-flag check. We hide entirely if not available,
  // so the bubble never renders for tenants with feature off / cap=0.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const a = await getAiAvailability();
        if (cancelled) return;
        if (!a.available) {
          setPhase({ kind: "unavailable", reason: a.reason });
          return;
        }
        setPhase({ kind: "ready", availability: a });
        // First-login tooltip — show after 3s, dismiss after 8s, suppress
        // for 30 days after user closes. Per §6.1.
        const dismissedAt = Number(localStorage.getItem(TOOLTIP_DISMISSED_AT) ?? "0");
        const now = Date.now();
        const SUPPRESS_MS = 30 * 24 * 60 * 60 * 1000;
        if (now - dismissedAt > SUPPRESS_MS) {
          const t1 = window.setTimeout(() => setShowTooltip(true), 3000);
          const t2 = window.setTimeout(() => setShowTooltip(false), 11000);
          return () => { clearTimeout(t1); clearTimeout(t2); };
        }
      } catch {
        // If availability check itself fails (network, 401, etc.), hide
        // the bubble silently. Don't surface scary errors before user
        // even opens the chat.
        if (!cancelled) setPhase({ kind: "unavailable", reason: "availability_check_failed" });
      }
    })();
    return () => { cancelled = true; };
  }, [session.role]);

  // Render nothing while loading or unavailable. The bubble is an
  // enhancement, not core navigation; better silent than a flicker.
  //
  // Exception: inline variant IS the AI 導覽 page's central content,
  // so returning null leaves a blank page. Show an explicit fallback
  // panel instead so the operator knows the chat is loading /
  // disabled rather than thinking the dashboard broke.
  if (phase.kind !== "ready") {
    if (variant === "inline") {
      const reason = phase.kind === "loading_availability"
        ? "正在準備 AI 助手…"
        : phase.reason === "feature_disabled"
          ? "您的單位目前未啟用 SENTRY 助手。請聯絡您的管理員。"
          : phase.reason === "quota_zero"
            ? "您的單位目前未開放 SENTRY 助手用量。請聯絡您的管理員。"
            : phase.reason === "availability_check_failed"
              ? "暫時無法載入 AI 助手，請稍後再試。"
              : `AI 助手暫時無法使用${phase.reason ? `（${phase.reason}）` : ""}`;
      return (
        <div className="relative w-full h-full bg-bg-surface flex flex-col items-center justify-center px-6 py-8 text-center">
          <Sparkles className="h-8 w-8 text-text-muted mb-3" />
          <p className="text-sm text-text-secondary leading-relaxed max-w-xs">{reason}</p>
        </div>
      );
    }
    return null;
  }

  const dismissTooltip = () => {
    setShowTooltip(false);
    localStorage.setItem(TOOLTIP_DISMISSED_AT, String(Date.now()));
  };

  /** Geometry helpers — translate `BubblePos` (snap side + viewport-Y
   *  ratio) into concrete `left` / `top` px for the fixed-positioned
   *  bubble + tooltip. SSR-safe: returns sensible defaults when window
   *  isn't available. */
  const bubbleGeometry = ((): { left: number; top: number; side: "left" | "right" } => {
    if (typeof window === "undefined") {
      // Default to old "bottom-6 right-6" shape — server render won't
      // actually paint this; just keep TS happy + a fallback for tests.
      return { left: 1000, top: 800, side: "right" };
    }
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (dragOffset) {
      // Mid-drag — follow pointer (already clamped on pointermove).
      return { left: dragOffset.x, top: dragOffset.y, side: bubblePos.side };
    }
    const top = Math.max(EDGE_GAP_PX, Math.min(H - BUBBLE_DIAMETER_PX - EDGE_GAP_PX, bubblePos.yRatio * H));
    const left = bubblePos.side === "left" ? EDGE_GAP_PX : W - BUBBLE_DIAMETER_PX - EDGE_GAP_PX;
    return { left, top, side: bubblePos.side };
  })();

  const onBubblePointerDown = (e: React.PointerEvent<HTMLButtonElement>): void => {
    if (e.button !== 0 && e.pointerType === "mouse") return; // primary button only
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    dragMovedRef.current = false;
    dragStartRef.current = {
      pointerId: e.pointerId,
      // Where in the bubble did the pointer land? Preserve so the
      // bubble doesn't snap its top-left to the cursor on the first
      // pointermove.
      offsetX: e.clientX - bubbleGeometry.left,
      offsetY: e.clientY - bubbleGeometry.top,
    };
  };

  const onBubblePointerMove = (e: React.PointerEvent<HTMLButtonElement>): void => {
    const start = dragStartRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    const dx = Math.abs(e.clientX - (bubbleGeometry.left + start.offsetX));
    const dy = Math.abs(e.clientY - (bubbleGeometry.top + start.offsetY));
    if (!dragMovedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    dragMovedRef.current = true;
    const W = window.innerWidth;
    const H = window.innerHeight;
    const rawX = e.clientX - start.offsetX;
    const rawY = e.clientY - start.offsetY;
    // Clamp so the bubble can't be dragged off-screen.
    const x = Math.max(0, Math.min(W - BUBBLE_DIAMETER_PX, rawX));
    const y = Math.max(EDGE_GAP_PX, Math.min(H - BUBBLE_DIAMETER_PX - EDGE_GAP_PX, rawY));
    setDragOffset({ x, y });
  };

  const onBubblePointerUp = (e: React.PointerEvent<HTMLButtonElement>): void => {
    const start = dragStartRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    dragStartRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* may not be capturing */ }

    if (!dragMovedRef.current) {
      // Plain tap — let the bubble's onClick handle expand.
      setDragOffset(null);
      return;
    }
    // Drag finished — snap to nearest vertical edge. Use the centre of
    // the bubble vs centre of the viewport so a pointer that ends up
    // exactly on the midline (rare) doesn't oscillate.
    const W = window.innerWidth;
    const H = window.innerHeight;
    const offset = dragOffset ?? { x: bubbleGeometry.left, y: bubbleGeometry.top };
    const centreX = offset.x + BUBBLE_DIAMETER_PX / 2;
    const side: "left" | "right" = centreX < W / 2 ? "left" : "right";
    const yRatio = offset.y / H;
    setBubblePos({ side, yRatio });
    setDragOffset(null);
  };

  return (
    <>
      {/* Floating tooltip — 「需要協助設定嗎？」 (OQ C3). Positioned
       *  relative to the bubble so it lives wherever the operator
       *  parked the bubble (left-edge / right-edge / mid-screen). */}
      {!expanded && showTooltip && (() => {
        const W = typeof window !== "undefined" ? window.innerWidth : 1200;
        // Tooltip sits ABOVE the bubble with a small gap, aligned to
        // the same edge so it stays on-screen regardless of bubble side.
        const tooltipStyle: React.CSSProperties = {
          position: "fixed",
          top: Math.max(EDGE_GAP_PX, bubbleGeometry.top - 64),
          zIndex: 9000,
          maxWidth: 260,
          ...(bubbleGeometry.side === "left"
            ? { left: EDGE_GAP_PX }
            : { left: Math.max(EDGE_GAP_PX, W - 260 - EDGE_GAP_PX) }),
        };
        return (
          <button
            type="button"
            onClick={() => { setExpanded(true); setChatOpening(true); dismissTooltip(); }}
            style={tooltipStyle}
            className="rounded-lg bg-bg-surface border border-accent/40 px-3 py-2 text-xs text-text-primary shadow-lg animate-fade-in text-left"
          >
            <span className="block font-medium text-accent mb-0.5">需要協助設定嗎？</span>
            <span className="block text-text-secondary leading-snug">
              點這顆球，問我系統怎麼用。
            </span>
          </button>
        );
      })()}

      {/* Floating bubble — collapsed state. Draggable: pointerdown +
       *  move past DRAG_THRESHOLD_PX = drag mode (bubble follows pointer,
       *  click suppressed); pointerup snaps to nearest vertical edge
       *  with the same EDGE_GAP_PX gap as the default position. State
       *  persists across reloads via localStorage. */}
      {!expanded && (
        <button
          type="button"
          aria-label="開啟 SENTRY 助手"
          onClick={() => {
            // Browser fires click after pointerup even when the gesture
            // was actually a drag — suppress expand in that case so the
            // operator can park the bubble without accidentally opening
            // the chat. dragMovedRef is set by onBubblePointerMove and
            // reset on the next pointerdown.
            if (dragMovedRef.current) return;
            setExpanded(true);
            setChatOpening(true);
            dismissTooltip();
          }}
          onPointerDown={onBubblePointerDown}
          onPointerMove={onBubblePointerMove}
          onPointerUp={onBubblePointerUp}
          onPointerCancel={onBubblePointerUp}
          style={{
            position: "fixed",
            left: bubbleGeometry.left,
            top: bubbleGeometry.top,
            zIndex: 9000,
            // No transition while dragging (bubble must track pointer
            // 1:1); enable on release so the snap-to-edge animates.
            transition: dragOffset ? "none" : "left 200ms ease-out, top 200ms ease-out",
            touchAction: "none",
          }}
          className="h-12 w-12 rounded-full bg-accent text-bg-base shadow-lg hover:scale-105 active:scale-95 inline-flex items-center justify-center select-none cursor-grab active:cursor-grabbing"
        >
          <Sparkles className="h-5 w-5 pointer-events-none" />
        </button>
      )}

      {/* ChatWindow stays mounted across collapse/expand so the
       *  tour state (pendingTour / activeTour) lives across hiding
       *  the panel. Auto-closing the panel when a tour starts must
       *  NOT tear down the TourEngine. We hide via the `hidden` prop
       *  → display:none, which preserves all React state and effects. */}
      <ChatWindow
        availability={phase.availability}
        variant={variant}
        hidden={variant === "inline" ? false : !expanded}
        animState={variant === "inline" ? "idle" : chatClosing ? "closing" : chatOpening ? "opening" : "idle"}
        onPlanApplyComplete={onPlanApplyComplete}
        planHost={planHost}
        onActivePlanChange={onActivePlanChange}
        onActiveDrawerFormChange={onActiveDrawerFormChange}
        onDrawerFormComplete={onDrawerFormComplete}
        onClose={() => setExpanded(false)}
      />
    </>
  );
}

// ─── ChatWindow ────────────────────────────────────────────────────────────

interface ChatWindowProps {
  availability: AiAvailability;
  /** When true, the panel chrome is hidden via display:none but the
   *  component stays mounted so internal state (activeTour, threads,
   *  streaming, etc.) survives. Critical for the
   *  「點開引導 → 自動關閉聊天 → tour 持續執行」 flow. */
  hidden?: boolean;
  /** Drives the entrance / exit animation:
   *   - "opening" → applies `animate-chat-pop-in` keyframe (scale +
   *     translateY entrance, ~220 ms). Set transiently when the
   *     operator clicks the floating bubble.
   *   - "closing" → applies `animate-chat-slide-out` keyframe
   *     (translateY + opacity exit, ~200 ms). Set on mobile when an
   *     inline quick-nav button is clicked; the panel slides off
   *     screen before the route swap finishes so the destination page
   *     isn't briefly covered.
   *   - "idle" → no keyframe, panel sits in its committed state.
   *  Caller (ChatBubble) clears the flag via timer after the keyframe
   *  duration. */
  animState?: "idle" | "opening" | "closing";
  /** Layout variant — "floating" uses fixed bottom-right positioning;
   *  "inline" expects to be sized by the parent (relative + 100%
   *  width/height). Spec §4.5 inline mode for AI 導覽 page. */
  variant?: "floating" | "inline";
  /** Forwarded from ChatBubble.props — fires AFTER PlanModal close so
   *  the host page can refetch + highlight newly-created entities. */
  onPlanApplyComplete?: (results: import("./PlanModal.js").OpResult[]) => void;
  /** AM-5b — when "external" the host renders PlanPanel itself
   *  (typically in the AI 導覽 drawer) and ChatBubble skips its
   *  floating PlanModal. Default "modal" preserves legacy behaviour. */
  planHost?: "modal" | "external";
  /** AM-5b — fires whenever ChatBubble's activePlan transitions; only
   *  meaningful when planHost === "external". */
  onActivePlanChange?: (info: ActivePlanInfo | null) => void;
  /** AM-7 — propagated from ChatBubbleProps. */
  onActiveDrawerFormChange?: (info: ActiveDrawerFormInfo | null) => void;
  onDrawerFormComplete?: (outcome: import("./DrawerFormHost.js").DrawerFormOutcome) => void;
  onClose: () => void;
}

function ChatWindow({
  availability, hidden, animState = "idle", variant = "floating",
  onPlanApplyComplete, planHost = "modal", onActivePlanChange,
  onActiveDrawerFormChange, onDrawerFormComplete, onClose,
}: ChatWindowProps): React.ReactElement {
  const session = useDashboardSession();
  // Guest (no tenant binding) can't use AI chat at all — composer + send
  // button must be locked so they don't burn /v1/ai/threads requests
  // against a session that'll 401/403 anyway. Placeholder copy redirects
  // them to the actual fix (LINE binding code).
  const isGuest = session.role === "guest";
  // AM-6: AI 模式禁用 propose_tour（前後端 belt-and-suspenders — system
  // prompt 也教 AI 不要 emit。若仍漏掉，這裡 silently 自動 reject 並
  // post tool_result 回 AI，讓它知道要改用 propose_quick_nav 或建議
  // 切到專業模式）。
  const { mode: aiMode } = useAiMode();
  // Touch-device detection for composer Enter behaviour. On desktop
  // (hover + fine pointer) Enter sends + Shift+Enter inserts newline
  // — established power-user convention. On touch devices (phone /
  // tablet on-screen keyboard) there's no Shift+Enter combo, the
  // "send" affordance is the dedicated button to the right of the
  // textarea, and operators expect Enter = newline like every other
  // mobile chat app. Detection uses `pointer: coarse` not viewport
  // width — iPad in landscape exceeds the md breakpoint but still
  // needs the touch convention. Static check (no resize listener);
  // hot-swapping a hardware keyboard mid-session is rare enough not
  // to warrant the complexity.
  const isTouchDevice = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(pointer: coarse)").matches;
  }, []);
  // Inline-variant chat header reads the same AiConsoleSlot the global
  // Header consumes on desktop; on mobile (md hidden in Header) we
  // render it here in the chat header's right cluster so the operator
  // doesn't get a separate IconRow strip above the chat.
  const { slot: aiConsoleSlot } = useAiConsoleSlot();
  // When the operator is a super_admin impersonating a tenant we have
  // an `acting_tenant_id` on the session; in that case PlanModal must
  // forward it to each write endpoint so the data lands in the right
  // tenant's shard. Regular tenant users pass null (server reads from
  // their own session). Mirrors the pattern in pages like Tasks.tsx.
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const [threads, setThreads] = useState<ThreadsState>({ kind: "idle" });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState>({ kind: "idle" });
  const [composing, setComposing] = useState("");
  const [streaming, setStreaming] = useState<StreamingAssistant | null>(null);
  const [streamErr, setStreamErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  /** Per-tenant monthly token usage shown as a single percentage strip
   *  at the top of the chat panel. Spec: docs/specs/ai-token-quota.md
   *  §7.1. The bubble doesn't render raw token counts (噪音) — only
   *  the % bar, and full numbers live on /dashboard/ai-usage. */
  const [usedMonthTokens, setUsedMonthTokens] = useState(availability.used_month_tokens);
  const capMonthTokens = availability.cap_month_tokens;
  const percentUsed = capMonthTokens > 0
    ? Math.min(100, Math.round((usedMonthTokens / capMonthTokens) * 100))
    : 0;
  const quotaExhausted = capMonthTokens >= 0 && usedMonthTokens >= capMonthTokens;
  // Click-through to detail page gated by read:billing scope (same as
  // /dashboard/billing). Non-billing users see the % strip read-only.
  const navigate = useNavigate();
  const canSeeUsageDetail = session.scopes.includes("read:billing");

  // ── Scroll-to-bottom affordance ───────────────────────────────────
  // The messages pane auto-scrolls to bottom when new content arrives
  // (MessageList's endRef.scrollIntoView). When the operator scrolls
  // up to read older context, an overlay button appears bottom-right
  // so they can return to「最新訊息」in one tap. Hidden by default
  // (initial mount IS at bottom).
  const messagesScrollRef = useRef<HTMLElement>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const onScroll = (): void => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      // 80 px hysteresis — single-tap "back to bottom" feels natural;
      // a sub-line distance shouldn't reveal the button.
      setShowScrollDown(distFromBottom > 80);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // initial check
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  const scrollMessagesToBottom = useCallback((): void => {
    const el = messagesScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  // AI 導覽抽屜關閉後，chat 區域往下展開（mobile 從 ~45vh → 100vh）—
  // 自動把訊息卷到底，操作員可立即看到剛 inject 的 LocalCard（例：邀請
  // 碼 QR）。Dispatch 來自 AiConsoleDrawer 的 useEffect。
  useEffect(() => {
    const handler = (): void => {
      // 給 layout 一個 frame 讓 chat 容器先 resize，再 scroll，否則
      // scrollHeight 還是舊的（壓縮在 45vh 內）值。
      requestAnimationFrame(() => scrollMessagesToBottom());
    };
    window.addEventListener("ai-console-drawer:closed", handler);
    return () => window.removeEventListener("ai-console-drawer:closed", handler);
  }, [scrollMessagesToBottom]);
  /** Tool name currently being awaited from the AI (during stream) or
   *  executed browser-side. Shown above the composer as 「AI 正在查
   *  list_sites…」status. Null when no tool is in flight. */
  const [activeToolName, setActiveToolName] = useState<string | null>(null);
  const dlg = useConfirmDialog();
  const abortRef = useRef<AbortController | null>(null);
  /** Accumulator for tool_call SSE events during a single turn. Drained
   *  after the turn's final event so we can batch-execute all calls. */
  const pendingCallsRef = useRef<SseToolCall[]>([]);
  /** IME composition guard. Tracks whether the textarea is mid-IME-
   *  composition (注音 / 拼音 / Kana / 한글 etc.). Three-layer protection
   *  inside onKeyDown — see the input handler for browser-quirk notes. */
  const composingRef = useRef(false);
  /** Set true by send() right before it calls setActiveId on a fresh
   *  thread. The activeId effect checks this flag and, when set, skips
   *  the setDetail({loading}) + getAiThread reload so the optimistic
   *  user bubble already shown by send() isn't clobbered. The effect
   *  resets it after honouring. Plain string holds the thread_id we
   *  want to skip — extra paranoia that we only skip the intended one. */
  const skipDetailLoadForThreadRef = useRef<string | null>(null);
  const screenMeta = useScreenMeta();

  /** AI emitted propose_tour → store the proposal until user accepts /
   *  rejects via the confirm modal. Includes the thread_id + message_id
   *  needed by /v1/ai/tours/:run_id/start. */
  const [pendingTour, setPendingTour] = useState<
    | null
    | (AiTourProposal & { thread_id: string; assistant_message_id: string })
  >(null);
  /** Tour currently running in the overlay engine. */
  const [activeTour, setActiveTour] = useState<
    | null
    | (AiTourProposal & { thread_id: string; assistant_message_id: string })
  >(null);
  /** Accumulator for propose_tour proposals seen this turn — keyed by
   *  call_id so multiple proposals in one turn are handled (rare; AI
   *  usually proposes one tour per turn). */
  const pendingProposalsRef = useRef<AiTourProposal[]>([]);

  /** Active plan modal (AI Plan & Apply, docs/specs/ai-plan-and-apply.md).
   *  When set the modal renders over everything; clearing it (via the
   *  onClose callback) posts a tool_result back to the AI thread so the
   *  model can acknowledge. Only one plan modal at a time per session —
   *  if a second propose_plan event arrives while one is active we
   *  silently drop it (rare; AI rarely chains plans). */
  const [activePlan, setActivePlan] = useState<{
    plan: PlanProposal;
    thread_id: string;
  } | null>(null);

  /** AM-7 active drawer form. Mirrors activePlan: one form at a time;
   *  if a second propose_drawer_form arrives while one is active we
   *  silently drop it (AI is instructed to emit one per turn). */
  const [activeDrawerForm, setActiveDrawerForm] = useState<{
    kind: import("../lib/chatApi.js").DrawerFormKind;
    call_id: string;
    thread_id: string;
    prefill: Record<string, unknown>;
    reason: string;
  } | null>(null);

  /** Volatile set of propose_questions call_ids the operator has
   *  explicitly dismissed via the「關閉」 button. Used to flip those
   *  cards to a read-only「已關閉」 summary even though the persisted
   *  tool_calls JSON still describes them as pending. Cleared on page
   *  refresh — by then the server-side orphan-resolver has filled the
   *  `{cancelled:true, reason:'user_skipped'}` sentinel and the user
   *  has likely moved on; the card briefly re-rendering interactive on
   *  re-visit is acceptable cost vs the alternative (writing a
   *  separate persisted dismissal state that overlaps with the
   *  orphan-resolver). */
  const [dismissedQuestionStacks, setDismissedQuestionStacks] = useState<Set<string>>(() => new Set());

  /** Local-only cards inserted into the messages list AFTER an
   *  activeDrawerForm completes with a credential-bearing outcome
   *  (issue_binding_code). NOT persisted, NEVER sent to OpenAI — when
   *  the user reloads or switches thread the cards disappear. The
   *  8-character code is effectively a short-lived credential; logging
   *  it to the message history would expose it on every subsequent
   *  /v1/ai/messages turn. Operator can still re-retrieve the code
   *  history from /dashboard/admin/binding-codes if needed. */
  const [localCards, setLocalCards] = useState<Map<string, LocalCard>>(() => new Map());

  // Load threads on mount.
  const loadThreads = useCallback(async () => {
    setThreads({ kind: "loading" });
    try {
      const r = await listAiThreads(50);
      setThreads({ kind: "ready", items: r.items });
      // Auto-select most recent if nothing picked.
      if (!activeId && r.items.length > 0) {
        setActiveId(r.items[0].id);
      }
    } catch (err) {
      setThreads({ kind: "error", message: friendlyChatError(err) });
    }
  }, [activeId]);

  useEffect(() => { void loadThreads(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // AI 模式 sidebar 與這層元件離得很遠（Sidebar → AppLayout → Topology →
  // ChatBubble），用 window CustomEvent 跨層通知 (同 `ai-console-drawer:
  // closed` pattern)。四個事件：
  //   ai:new-work                    — sidebar 「新 AI 工作」 點擊
  //   ai:select-work {id}            — sidebar 「最近工作」 選擇
  //   ai:works-changed {archivedId?} — 任一邊改了 thread 列表（新增 / 封
  //                                    存），雙邊 reload 同步
  //   ai:active-work-changed {id}    — ChatBubble → Sidebar，廣播目前
  //                                    activeId 以便 sidebar 高亮對應列
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("ai:active-work-changed", { detail: { id: activeId } }));
  }, [activeId]);

  useEffect(() => {
    const onNew = () => { void newThread(); };
    const onSelect = (e: Event) => {
      const id = (e as CustomEvent<{ id?: string }>).detail?.id;
      if (typeof id === "string" && id.length > 0) setActiveId(id);
    };
    const onChanged = (e: Event) => {
      const archivedId = (e as CustomEvent<{ archivedId?: string }>).detail?.archivedId;
      if (archivedId && archivedId === activeId) {
        setActiveId(null);
        setDetail({ kind: "idle" });
      }
      void loadThreads();
    };
    window.addEventListener("ai:new-work", onNew);
    window.addEventListener("ai:select-work", onSelect);
    window.addEventListener("ai:works-changed", onChanged);
    return () => {
      window.removeEventListener("ai:new-work", onNew);
      window.removeEventListener("ai:select-work", onSelect);
      window.removeEventListener("ai:works-changed", onChanged);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [activeId]);

  // Load thread detail when selection changes.
  useEffect(() => {
    if (!activeId) {
      setDetail({ kind: "idle" });
      return;
    }
    // Brand-new-thread send: send() already optimistic-rendered the
    // user bubble + streaming placeholder under a fresh thread id.
    // Skip the reload so we don't flash the bubble away while
    // getAiThread fetches an essentially-empty row (user message
    // is still in flight to the server).
    if (skipDetailLoadForThreadRef.current === activeId) {
      skipDetailLoadForThreadRef.current = null;
      return;
    }
    let cancelled = false;
    setDetail({ kind: "loading" });
    void (async () => {
      try {
        const d = await getAiThread(activeId);
        if (cancelled) return;
        setDetail({ kind: "ready", thread: d.thread, messages: d.messages });
        // Partial-state recovery: if the previous turn's stream broke
        // before the propose_tour SSE event reached the browser, the
        // proposal still lives in the assistant message's tool_calls
        // JSON. Walk the messages, surface an orphan proposal as
        // pendingTour so the modal appears on next thread load.
        // AM-6: AI 模式整段跳過 (tour 禁用)。
        const orphan = aiMode === "ai" ? null : findOrphanProposal(d.messages);
        if (orphan && !pendingTour && !activeTour) {
          setPendingTour({
            ...orphan,
            thread_id: d.thread.id,
            assistant_message_id: orphan.assistant_message_id,
          });
        }
      } catch (err) {
        if (cancelled) return;
        setDetail({ kind: "error", message: friendlyChatError(err) });
      }
    })();
    return () => { cancelled = true; };
  }, [activeId]);

  const newThread = async () => {
    setCreating(true);
    try {
      const t = await createAiThread();
      setThreads((s) => s.kind === "ready" ? { kind: "ready", items: [t, ...s.items] } : s);
      setActiveId(t.id);
      window.dispatchEvent(new CustomEvent("ai:works-changed"));
    } catch (err) {
      setStreamErr(friendlyChatError(err));
    } finally {
      setCreating(false);
    }
  };

  const deleteThread = async (id: string) => {
    if (!(await dlg.confirm("封存此對話？對話內容會立即從列表消失（管理員仍可從操作紀錄還原）。", { destructive: true, okLabel: "封存" }))) return;
    try {
      await deleteAiThread(id);
      setThreads((s) =>
        s.kind === "ready" ? { kind: "ready", items: s.items.filter((t) => t.id !== id) } : s,
      );
      if (activeId === id) {
        setActiveId(null);
        setDetail({ kind: "idle" });
      }
      window.dispatchEvent(new CustomEvent("ai:works-changed", { detail: { archivedId: id } }));
    } catch (err) {
      setStreamErr(friendlyChatError(err));
    }
  };

  const send = async () => {
    const text = composing.trim();
    if (!text || sending) return;
    setStreamErr(null);

    // Synchronous UX commit BEFORE any network call. Operator just hit
    // send — their bubble + the streaming placeholder must appear
    // immediately, otherwise the chat looks frozen until createAiThread
    // round-trips (first send of a brand-new thread). Per user report:
    // 「有時送出 AI 對話 要等系統反應到一個程度 對話視窗才會顯示我送出
    // 的 prompt」.
    setSending(true);
    setComposing("");
    setStreaming({ text: "", user_message_id: "pending", startedAt: new Date().toISOString() });
    const optimisticUserMsg: AiMessageRow = {
      id: `local:${Date.now()}`,
      thread_id: activeId ?? "pending",
      role: "user",
      content: text,
      tool_calls: null,
      tool_call_id: null,
      tokens_in: null,
      tokens_out: null,
      screen_context: null,
      created_at: new Date().toISOString(),
    };
    setDetail((d) => {
      if (d.kind === "ready") return { kind: "ready", thread: d.thread, messages: [...d.messages, optimisticUserMsg] };
      // Brand-new thread (no detail yet) — synthesise a placeholder
      // ready state so the user's bubble renders. getAiThread fires
      // in the activeId effect once the thread is created and will
      // reconcile thread + messages to the canonical server state.
      const placeholderThread: AiThreadRow = {
        id: optimisticUserMsg.thread_id,
        user_id: "",
        tenant_id: null,
        title: text.slice(0, 60),
        openai_last_response_id: null,
        message_count: 1,
        total_tokens_used: 0,
        created_at: optimisticUserMsg.created_at,
        updated_at: optimisticUserMsg.created_at,
        archived_at: null,
      };
      return { kind: "ready", thread: placeholderThread, messages: [optimisticUserMsg] };
    });

    // Ensure we have an active thread; lazy-create on first send. The
    // user already sees their bubble (above); this just resolves the
    // backing thread id before we kick off the SSE stream.
    let threadId = activeId;
    if (!threadId) {
      try {
        const t = await createAiThread();
        threadId = t.id;
        // Reconcile the placeholder thread id in our optimistic detail
        // to the real server-assigned one BEFORE setActiveId, so the
        // single render where activeId changes is already coherent.
        setDetail((d) => {
          if (d.kind !== "ready") return d;
          return {
            kind: "ready",
            thread: { ...d.thread, id: t.id, user_id: t.user_id, tenant_id: t.tenant_id, created_at: t.created_at, updated_at: t.updated_at },
            messages: d.messages.map((m) => m.thread_id === "pending" ? { ...m, thread_id: t.id } : m),
          };
        });
        // Tell the activeId effect to skip its reload — we already have
        // the right detail rendered.
        skipDetailLoadForThreadRef.current = t.id;
        setActiveId(t.id);
        setThreads((s) => s.kind === "ready" ? { kind: "ready", items: [t, ...s.items] } : s);
      } catch (err) {
        setStreaming(null);
        setSending(false);
        setStreamErr(friendlyChatError(err));
        return;
      }
    }

    abortRef.current = new AbortController();
    try {
      await runChainedTurn(
        { threadId, kind: "user", text },
        abortRef.current.signal,
      );
      // Token tally bump happens in onFinal SSE handler (we have the
      // real prompt+output token count there, not a +1 message guess).
      void loadThreads();
    } catch (err) {
      setStreaming(null);
      setStreamErr(friendlyChatError(err));
      // Stream broke (network drop, Cloudflare proxy idle timeout, OpenAI
      // hiccup). Re-fetch the thread so any propose_tour the AI managed
      // to emit before the disconnect — but whose SSE event never reached
      // us — is recovered from `tool_calls` JSON via findOrphanProposal()
      // in the activeId effect. The user sees the modal pop on its own.
      const tid = activeId;
      if (tid) {
        void getAiThread(tid).then((d) => {
          setDetail({ kind: "ready", thread: d.thread, messages: d.messages });
          // AM-6: AI 模式跳過 orphan-proposal 復原 (tour 禁用)。
          const orphan = aiMode === "ai" ? null : findOrphanProposal(d.messages);
          if (orphan && !pendingTour && !activeTour) {
            setPendingTour({
              ...orphan,
              thread_id: d.thread.id,
              assistant_message_id: orphan.assistant_message_id,
            });
          }
        }).catch(() => { /* tolerate — error banner already shown */ });
      }
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  /** Run one turn of the AI conversation. If the AI emits function_calls,
   *  execute them browser-side and POST results back, recursing into
   *  another turn — until the AI replies with plain text (no
   *  pending_tool_calls). Max 6 iterations to prevent runaway loops. */
  const runChainedTurn = async (
    initial: { threadId: string; kind: "user"; text: string } | { threadId: string; kind: "tool_results"; results: ToolResultPayload[] },
    signal: AbortSignal,
  ): Promise<void> => {
    const MAX_HOPS = 6;
    let hop = 0;
    let nextStart: typeof initial = initial;
    // Reset cross-turn accumulators so a previous failed turn can't leak
    // stale proposals / pending calls into this one.
    pendingProposalsRef.current = [];
    pendingCallsRef.current = [];

    while (hop < MAX_HOPS) {
      hop += 1;
      let finalInfo: SseFinal | null = null;

      const events: SendMessageEvents = {
        onToken: (chunk) => {
          setStreaming((s) => s ? { ...s, text: s.text + chunk } : s);
        },
        onToolCallStarted: (info) => {
          setActiveToolName(info.name);
        },
        onToolCall: (call) => {
          // We accumulate calls during the stream — actual execution
          // happens after the final event arrives so we have the full
          // set in one batch.
          pendingCallsRef.current.push(call);
        },
        onProposeTour: (prop) => {
          // Tour proposals are handled out-of-band from tool calls —
          // they go to a confirm modal first, then optionally run the
          // tour engine, then loop back with a tool_result reporting
          // the outcome. Collect now; pop after the turn's final event.
          pendingProposalsRef.current.push(prop);
        },
        onProposeQuickNav: (prop) => {
          // Quick-nav buttons render LIVE on the streaming bubble (so
          // the user can tap before AI even finishes its prose). After
          // the final event, the same items are also persisted via
          // tool_calls JSON on the assistant message row — that lets
          // chat-history re-renders surface them too.
          setStreaming((s) => s ? { ...s, quick_nav: prop.items } : s);
        },
        onProposeQuestions: (prop) => {
          // Multi-question picker stack. Append to the streaming bubble
          // so the operator can tap immediately; post-stream the stack
          // re-renders from tool_calls JSON via MessageBubble. Operator
          // submits all answers in one go; handleQuestionsSubmit POSTs
          // the tool_result back so AI's chain resumes.
          setStreaming((s) => s ? { ...s, questionStacks: [...(s.questionStacks ?? []), prop] } : s);
        },
        onProposePlan: (prop) => {
          // Plan modal opens immediately (no waiting for AI prose to
          // finish — the modal is the primary UI for this turn). Only
          // one plan at a time; if another arrives we silently drop
          // (rare; AI is instructed to emit one plan per turn).
          if (activePlan) return;
          setActivePlan({ plan: prop, thread_id: nextStart.threadId });
        },
        onProposeDrawerForm: (prop) => {
          // AM-7 — drawer create-form. Only meaningful in inline
          // variant (the drawer concept only exists on the AI 導覽
          // page). Floating mode silently drops + posts a cancellation
          // tool_result so the AI chain stays consistent.
          if (variant !== "inline") {
            void postToolResultsAsync(nextStart.threadId, [{
              call_id: prop.call_id,
              output: {
                accepted: false,
                reason: "drawer_unavailable_in_pro_mode",
                hint: "propose_drawer_form 只在 AI 模式有效。改用 propose_quick_nav 帶使用者到目標頁，或建議使用者切換到 AI 模式。",
              },
            }]);
            return;
          }
          if (activeDrawerForm) return; // one form at a time
          setActiveDrawerForm({
            kind: prop.kind,
            call_id: prop.call_id,
            thread_id: nextStart.threadId,
            prefill: prop.prefill,
            reason: prop.reason,
          });
        },
        onProposeFocusTopology: (prop) => {
          // AM-5c — fire-and-forget. Browser-side dispatch only takes
          // effect when the operator is in AI mode + already on the
          // AI 導覽 page. We navigate to /dashboard/topology with the
          // focus params; the page reads them and applies the filter.
          // If the operator is in pro mode the navigate still lands
          // them on the full topology canvas (the page interprets
          // ?focus= in pro mode too — just no drawer, so the canvas
          // itself filters down to the focused subset).
          const params = new URLSearchParams();
          params.set("focus", prop.entity_ids.join(","));
          // ?drawer=topology so the drawer auto-opens to the canvas
          // view (caller wants visual companion to the prose). Topology
          // page tolerates this in pro mode (the URL param is ignored
          // when the drawer concept doesn't apply).
          params.set("drawer", "topology");
          if (prop.reason) params.set("focus_reason", prop.reason);
          navigate({ pathname: "/dashboard/topology", search: `?${params.toString()}` });
        },
        onFinal: (info) => {
          finalInfo = info;
          // Optimistic monthly token tally bump — next /availability
          // poll (on bubble re-mount) reconciles to the canonical
          // server sum. Tokens_in/out come from the SSE final event
          // which echoes OpenAI's diagnostic usage block.
          const delta = (info.tokens_in ?? 0) + (info.tokens_out ?? 0);
          if (delta > 0) setUsedMonthTokens((u) => u + delta);
        },
        onError: (info) => {
          setStreaming(null);
          setStreamErr(`AI 回應失敗：${info.message || info.code}`);
        },
      };

      if (nextStart.kind === "user") {
        const meta = screenMeta.getSnapshotMin();
        await sendAiMessage(
          { threadId: nextStart.threadId, text: nextStart.text, screen_context: meta as unknown as Record<string, unknown> },
          events,
          signal,
        );
      } else {
        await sendAiToolResults(nextStart.threadId, nextStart.results, events, signal);
      }

      // ── Drain propose_tour proposals FIRST ────────────────────────────
      // This must happen before the finalInfo branch because the SSE
      // propose_tour event arrives in-stream (before `final`), and the
      // stream can error out between propose_tour and final. If we only
      // popped proposals inside `if (finalInfo)`, an error mid-stream
      // would leave the proposal stranded → user sees the streamErr
      // banner but no confirm modal, no way to act on the tour.
      //
      // Each proposal carries its own call_id + run_id (server-minted)
      // so we don't need fInfo.message_id to surface the modal — we
      // attach a placeholder assistant_message_id when missing; tour
      // start tolerates that (user_ai_tour_runs.message_id is an
      // unindexed reference, not a FK).
      const proposals = pendingProposalsRef.current;
      pendingProposalsRef.current = [];
      if (proposals.length > 0) {
        // AM-6: AI 模式下 propose_tour 全數自動拒絕。Tour engine 多數
        // 步驟第一步是點 sidebar 某個 nav，AI 模式 sidebar 已換成「工
        // 作」列表 → 第一步必 404。乾脆全部 reject 並把原因回給 AI，
        // AI 下回知道要改用 propose_quick_nav 或建議使用者切到專業模式。
        if (aiMode === "ai") {
          for (const p of proposals) {
            void postToolResultsAsync(nextStart.threadId, [{
              call_id: p.call_id,
              output: {
                accepted: false,
                reason: "tour_disabled_in_ai_mode",
                hint: "AI 模式不支援 propose_tour（sidebar 已換成 AI 工作列表，多數 tour 步驟會 404）。改用 propose_quick_nav 帶使用者到目標頁，或於對話中建議切到專業模式再重新發問。",
              },
            }]);
          }
          setStreaming(null);
          setActiveToolName(null);
          pendingCallsRef.current = [];
          return;
        }
        const first = proposals[0];
        // finalInfo may be null when the stream errored between
        // propose_tour and the final event. Cast through to read
        // message_id when present; tour_runs row tolerates an empty
        // message_id reference.
        const fi = finalInfo as SseFinal | null;
        setPendingTour({
          ...first,
          thread_id: nextStart.threadId,
          assistant_message_id: fi?.message_id ?? "",
        });
        for (let i = 1; i < proposals.length; i++) {
          // Auto-reject extras — record them as rejected so the AI
          // chain stays consistent. (POST happens async; not awaited.)
          void postToolResultsAsync(nextStart.threadId, [{
            call_id: proposals[i].call_id,
            output: { accepted: false, reason: "extra_proposal_auto_rejected" },
          }]);
        }
        setStreaming(null);
        setActiveToolName(null);
        pendingCallsRef.current = [];
        return;
      }

      // Persist the assistant message that just streamed.
      if (finalInfo !== null) {
        const fInfo = finalInfo as SseFinal;
        const userMsgIdLocal = fInfo.user_message_id;
        setDetail((d) => {
          if (d.kind !== "ready") return d;
          let msgs = d.messages.filter((m) => !m.id.startsWith("local:"));
          // Append the canonical user message if server confirmed one.
          if (nextStart.kind === "user" && userMsgIdLocal) {
            msgs = [...msgs, {
              id: userMsgIdLocal,
              thread_id: nextStart.threadId,
              role: "user" as const,
              content: nextStart.text,
              tool_calls: null,
              tool_call_id: null,
              tokens_in: null,
              tokens_out: null,
              screen_context: null,
              created_at: new Date().toISOString(),
            }];
          }
          msgs = [...msgs, {
            id: fInfo.message_id,
            thread_id: nextStart.threadId,
            role: "assistant" as const,
            content: fInfo.content,
            tool_calls: fInfo.tool_calls.length > 0 ? JSON.stringify(fInfo.tool_calls) : null,
            tool_call_id: null,
            tokens_in: fInfo.tokens_in,
            tokens_out: fInfo.tokens_out,
            screen_context: null,
            created_at: new Date().toISOString(),
          }];
          return { kind: "ready", thread: d.thread, messages: msgs };
        });

        // If AI didn't request tools, we're done.
        if (!fInfo.pending_tool_calls || pendingCallsRef.current.length === 0) {
          setStreaming(null);
          setActiveToolName(null);
          pendingCallsRef.current = [];
          return;
        }

        // Execute the pending tool calls browser-side, then resume.
        const calls = pendingCallsRef.current;
        pendingCallsRef.current = [];
        const results: ToolResultPayload[] = [];
        for (const call of calls) {
          setActiveToolName(call.name);
          const r = await executeAiTool(call, {
            getScreenMetaRich: () => screenMeta.getSnapshotRich(),
          });
          // Append the tool result as a chat message so user sees the
          // round trip happened.
          setDetail((d) => d.kind === "ready" ? {
            kind: "ready",
            thread: d.thread,
            messages: [...d.messages, {
              id: `local-tool:${call.call_id}`,
              thread_id: nextStart.threadId,
              role: "tool" as const,
              content: typeof r.output === "string" ? r.output : JSON.stringify(r.output),
              tool_calls: null,
              tool_call_id: call.call_id,
              tokens_in: null,
              tokens_out: null,
              screen_context: null,
              created_at: new Date().toISOString(),
            }],
          } : d);
          results.push(r);
        }
        setActiveToolName(null);
        setStreaming({ text: "", user_message_id: "pending", startedAt: new Date().toISOString() });

        nextStart = { threadId: nextStart.threadId, kind: "tool_results", results };
        continue;
      }

      // No final event arrived (stream broke before completion) — bail.
      setStreaming(null);
      setActiveToolName(null);
      return;
    }

    // MAX_HOPS exceeded — surface but keep what we have.
    setStreamErr("AI 工具呼叫循環過多，已中斷。請開新對話。");
    setStreaming(null);
    setActiveToolName(null);
  };

  /** Post a batch of tool_results without resuming the chain. Used when
   *  auto-rejecting extra proposals or when we want to silently inform
   *  the AI of a side-result. We DO consume the SSE response but ignore
   *  its content — caller doesn't need it. */
  const postToolResultsAsync = async (
    threadId: string,
    results: ToolResultPayload[],
  ): Promise<void> => {
    try {
      await sendAiToolResults(threadId, results, {});
    } catch { /* swallow */ }
  };

  /** User accepted the tour confirm modal. Persist the run server-side
   *  then mount the TourEngine. Mirrors handleStartTourInline: also
   *  closes the chat panel so the tour overlay owns screen real
   *  estate (esp. on mobile where the chat panel is full-width). */
  const handleTourAccept = async (): Promise<void> => {
    if (!pendingTour) return;
    try {
      await startTourRun(pendingTour.run_id, {
        thread_id: pendingTour.thread_id,
        message_id: pendingTour.assistant_message_id,
        call_id: pendingTour.call_id,
        title: pendingTour.title,
        steps: pendingTour.steps,
      });
      setActiveTour(pendingTour);
      onClose();
    } catch (err) {
      setStreamErr(friendlyChatError(err));
    } finally {
      setPendingTour(null);
    }
  };

  /** Inline 「開始引導」 button (rendered inside the assistant bubble)
   *  was clicked. Skip the centered confirmation modal entirely — the
   *  bubble itself is the confirmation surface — and jump straight to
   *  the TourEngine. Same RTT as handleTourAccept (one POST start),
   *  but the centered modal step is collapsed away. */
  const handleStartTourInline = async (
    proposal: AiTourProposal & { thread_id: string; assistant_message_id: string },
  ): Promise<void> => {
    // ChatWindow knows the active thread; the inline button doesn't.
    const tid = activeId ?? proposal.thread_id;
    if (!tid) return;
    const fullProposal = { ...proposal, thread_id: tid };
    try {
      await startTourRun(fullProposal.run_id, {
        thread_id: fullProposal.thread_id,
        message_id: fullProposal.assistant_message_id,
        call_id: fullProposal.call_id,
        title: fullProposal.title,
        steps: fullProposal.steps,
      });
      setActiveTour(fullProposal);
      setPendingTour(null); // clear any auto-popup remnant
      // Auto-close the chat panel so the tour overlay isn't competing
      // with the chat-window for screen real estate (especially on
      // mobile where the chat panel is full-width). User can re-open
      // the bubble after the tour finishes if they want to chat again.
      onClose();
    } catch (err) {
      setStreamErr(friendlyChatError(err));
    }
  };

  /** User rejected the tour confirm modal. Tell AI the proposal was
   *  declined so it can react (e.g. ask if they want a written explanation
   *  instead). Don't write a tour_run row — none was started. */
  const handleTourReject = async (): Promise<void> => {
    if (!pendingTour) return;
    const proposal = pendingTour;
    setPendingTour(null);
    void resumeWithToolResult(proposal.thread_id, {
      call_id: proposal.call_id,
      output: { accepted: false, reason: "user_declined", run_id: proposal.run_id },
    });
  };

  /** Tour engine reached a terminal state. Persist outcome server-side
   *  then resume the AI chain with the tool_result so the model can
   *  respond accordingly ("great, you've finished!" / "let's try again"
   *  / etc.). */
  const handleTourComplete = async (
    outcome: "completed" | "cancelled" | "failed" | "paused",
    info: { steps_completed: number; failure_reason?: string },
  ): Promise<void> => {
    if (!activeTour) return;
    const tour = activeTour;
    setActiveTour(null);
    try {
      const result = await completeTourRun(tour.run_id, outcome, info.failure_reason);
      void resumeWithToolResult(tour.thread_id, {
        call_id: tour.call_id,
        output: {
          accepted: true,
          outcome: result.outcome,
          run_id: tour.run_id,
          steps_completed: result.steps_completed,
          steps_planned: result.steps_planned,
          failure_reason: result.failure_reason,
        },
      });
    } catch (err) {
      setStreamErr(friendlyChatError(err));
    }
  };

  // AM-5b: notify external host (AI 導覽 drawer) when activePlan
  // transitions. Fires on set + on clear; host renders PlanPanel using
  // the supplied close handler so the AI thread still receives the
  // tool_result on dismiss. No-op when planHost === "modal" (default
  // floating render path).
  useEffect(() => {
    if (planHost !== "external" || !onActivePlanChange) return;
    if (!activePlan) {
      onActivePlanChange(null);
      return;
    }
    onActivePlanChange({
      plan: activePlan.plan,
      threadId: activePlan.thread_id,
      tenantId: tenantParam,
      onClose: handlePlanClose,
    });
    // handlePlanClose closes over `activePlan` so we want it re-bound
    // every time activePlan changes; the host's PlanPanel keeps the
    // latest reference via the new callback delivery.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlan, planHost, tenantParam]);

  /** AM-7 drawer form completed — post the tool_result and clear local
   *  state. Saved outcome reports {accepted:true, entity_id, kind}; the
   *  AI typically chains a follow-up question ("要不要也派發任務上去？").
   *  Cancelled outcome reports {accepted:false, reason}. */
  const handleDrawerFormComplete = (outcome: import("./DrawerFormHost.js").DrawerFormOutcome): void => {
    if (!activeDrawerForm) return;
    const { call_id, thread_id } = activeDrawerForm;
    setActiveDrawerForm(null);
    let toolOutput: Record<string, unknown>;
    if (outcome.kind === "saved") {
      toolOutput = { accepted: true, kind: outcome.entity_kind, entity_id: outcome.entity_id };
    } else if (outcome.kind === "issued_binding_code") {
      // 8 字碼 = 短期 credential，絕不送進 OpenAI thread。本地 cards
      // map 存資料、稍後渲染為 non-persisted 卡片；AI 只知道 issued
      // 成功，看不到 code 本身。
      const cardId = `card-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const createdAt = new Date().toISOString();
      setLocalCards((prev) => {
        const next = new Map(prev);
        next.set(cardId, { kind: "issued_binding_code", thread_id, created_at: createdAt, issued: outcome.issued });
        return next;
      });
      toolOutput = { accepted: true, kind: "issue_binding_code", code_issued: true };
    } else {
      toolOutput = { accepted: false, reason: outcome.reason };
    }
    void postToolResultsAsync(thread_id, [{ call_id, output: toolOutput }]);
    onDrawerFormComplete?.(outcome);
  };

  /** Operator submitted answers on a propose_questions stack. Resume
   *  the AI chain with the full answer map so it can plan the next
   *  step with all decisions in hand. We use resumeWithToolResult
   *  (not postToolResultsAsync) so the chat visibly streams the AI's
   *  follow-up — the operator just made a decision, they expect a
   *  response. */
  const handleQuestionsSubmit = (
    call_id: string,
    answers: QuestionsAnswerMap,
  ): void => {
    const tid = activeId;
    if (!tid) return;
    void resumeWithToolResult(tid, {
      call_id,
      output: {
        accepted: true,
        answers,
      },
    });
  };

  /** Operator dismissed a propose_questions stack via「關閉」. We DON'T
   *  post a tool_result — the server-side orphan-resolver will fill
   *  `{cancelled:true, reason:'user_skipped'}` on the next user
   *  message so OpenAI's chain validation passes. But that sentinel
   *  is deliberately FILTERED OUT of resolvedCallIds (see line 2050
   *  comment) because it can fire before the user has seen the card.
   *  So if we relied on it the card would re-appear interactive after
   *  the stream's tool_calls JSON gets parsed post-stream.
   *
   *  Track dismissals in volatile component state so the affected card
   *  renders in「已關閉」 read-only mode. Volatile = lost on refresh,
   *  but by then the orphan-resolver has fired and the user has likely
   *  moved on; re-dismissing the card on next visit is cheap. */
  const handleQuestionsDismiss = (call_id: string): void => {
    setDismissedQuestionStacks((prev) => {
      if (prev.has(call_id)) return prev;
      const next = new Set(prev);
      next.add(call_id);
      return next;
    });
    setStreaming((s) => {
      if (!s) return s;
      const remaining = (s.questionStacks ?? []).filter((q) => q.call_id !== call_id);
      return { ...s, questionStacks: remaining };
    });
  };

  // AM-7 — propagate activeDrawerForm to host via onActiveDrawerFormChange.
  // Mirrors the AM-5b activePlan effect: fires on set + clear; host
  // renders DrawerFormHost using the supplied onComplete handler.
  useEffect(() => {
    if (variant !== "inline" || !onActiveDrawerFormChange) return;
    if (!activeDrawerForm) {
      onActiveDrawerFormChange(null);
      return;
    }
    onActiveDrawerFormChange({
      kind: activeDrawerForm.kind,
      callId: activeDrawerForm.call_id,
      threadId: activeDrawerForm.thread_id,
      tenantId: tenantParam,
      prefill: activeDrawerForm.prefill,
      reason: activeDrawerForm.reason,
      onComplete: handleDrawerFormComplete,
    });
    // handleDrawerFormComplete closes over activeDrawerForm; re-bound
    // every time activeDrawerForm changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrawerForm, variant, tenantParam]);

  /** AI Plan & Apply modal closed — final results bundle is what
   *  PlanModal hands back. Post a tool_result so the AI can acknowledge
   *  (「3 筆都成功了」/「2 筆 OK、1 筆失敗」). Per spec §15.6, we report
   *  per-op kind / status / error code but NOT server-assigned ids
   *  (ULIDs are noise to the operator + AI both). */
  const handlePlanClose = (results: import("./PlanModal.js").OpResult[]): void => {
    if (!activePlan) return;
    const plan = activePlan.plan;
    const thread_id = activePlan.thread_id;
    setActivePlan(null);
    const summary = results.map((r, i) => ({
      idx: i,
      kind: plan.operations[i].kind,
      display: plan.operations[i].display,
      status: r.kind,
      ...(r.kind === "fail"
        ? {
            error_code: r.errorCode,
            // Surface server-provided friendly message when distinct from the
            // code (e.g. roster overlap detail) so the AI can quote it back
            // verbatim instead of re-deriving from the bare code.
            ...(r.message && r.message !== r.errorCode ? { error_detail: r.message } : {}),
          }
        : {}),
    }));
    const success = summary.filter((s) => s.status === "success").length;
    const fail = summary.filter((s) => s.status === "fail").length;
    const skipped = summary.filter((s) => s.status === "skipped").length;
    void resumeWithToolResult(thread_id, {
      call_id: plan.call_id,
      output: {
        applied: true,
        plan_correlation_id: plan.plan_correlation_id,
        operations_total: plan.operations.length,
        operations_success: success,
        operations_failed: fail,
        operations_skipped: skipped,
        per_op: summary,
      },
    });
    // Host page hook — e.g. AI 導覽 page refetches topology + URL-
    // navigates with ?expanded= & ?highlight= so the operator sees the
    // newly-created nodes glow inside the structural graph. Spec
    // docs/specs/ai-mode-operations-console.md §5.
    if (onPlanApplyComplete) {
      try { onPlanApplyComplete(results); }
      catch (e) { console.error("onPlanApplyComplete threw:", e); }
    }
  };

  /** Helper: kick off a new chained-turn loop with tool_results, just
   *  like runChainedTurn but called from outside the send() context. */
  const resumeWithToolResult = async (
    threadId: string,
    result: ToolResultPayload,
  ): Promise<void> => {
    setSending(true);
    setStreaming({ text: "", user_message_id: "pending", startedAt: new Date().toISOString() });
    abortRef.current = new AbortController();
    try {
      await runChainedTurn(
        { threadId, kind: "tool_results", results: [result] },
        abortRef.current.signal,
      );
      void loadThreads();
    } catch (err) {
      setStreamErr(friendlyChatError(err));
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const cancelStream = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setSending(false);
    setStreaming(null);
    // Tell the server we cancelled — best-effort audit. The active SSE
    // is already torn down on our side; this just stamps the assistant
    // message row + writes ai.cancel for forensic value (PR #8).
    if (activeId) {
      void cancelAiTurn(activeId).catch(() => { /* swallow */ });
    }
  };

  // display:none only when truly idle-hidden. When animState is
  // "closing" we MUST keep the panel displayed for the slide-out
  // keyframe to play (parent flips hidden=true immediately after
  // dispatching the close event). When "opening" the panel was just
  // unhidden; same rendering constraint.
  const displayNone = hidden && animState === "idle";
  // Animation class — bottom-right transform-origin for pop-in so the
  // chat feels like it launches from the bubble's default corner.
  // (Operator might drag the bubble elsewhere; the 12 px scale + 12 px
  // translate is small enough that the origin mismatch isn't noticeable.)
  const animClass =
    animState === "opening" ? "animate-chat-pop-in origin-bottom-right" :
    animState === "closing" ? "animate-chat-slide-out" :
    "";
  // Wrapper class differs per variant:
  //   floating → fixed bottom-right, drag-positioned (legacy bubble UX)
  //   inline   → relative, fills the parent slot; host page sizes us
  //              (AI 導覽 page sets the bottom panel height).
  // Inline variant intentionally has no rounded corners / border /
  // shadow — the AI 導覽 page wraps both topology + this panel in
  // ONE outer rounded container with an internal divider so the
  // combined area reads as a single console rather than two cards
  // (AM-4.1 operator feedback). Spec §4.5.
  const wrapperClass = variant === "inline"
    ? `relative w-full h-full bg-bg-surface flex flex-col overflow-hidden`
    : `ai-rainbow-frame fixed z-[9000] bg-bg-surface rounded-xl shadow-2xl flex flex-col overflow-hidden
       bottom-6 right-6
       w-[400px] h-[min(80vh,720px)]
       max-md:bottom-[5vh] max-md:right-[5vw] max-md:left-[5vw]
       max-md:w-auto max-md:h-[75vh] ${animClass}`;
  return (
    <div
      role="dialog"
      aria-label="SENTRY 助手對話視窗"
      aria-hidden={hidden && animState !== "opening" ? "true" : undefined}
      style={displayNone && variant === "floating" ? { display: "none" } : undefined}
      className={wrapperClass}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-bg-base">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent" />
          <span className="font-medium text-sm">SENTRY 助手</span>
          {/* Per-tenant monthly token quota — only the percentage is
              shown (number not displayed inline; full breakdown lives
              under /dashboard/ai-usage). Click navigates there for
              read:billing scope holders; otherwise plain text. Spec
              docs/specs/ai-token-quota.md §7.1. */}
          {capMonthTokens > 0 && (
            <button
              type="button"
              onClick={canSeeUsageDetail ? () => navigate("/dashboard/ai-usage") : undefined}
              disabled={!canSeeUsageDetail}
              className={`ml-2 text-2xs inline-flex items-center gap-1 ${
                quotaExhausted ? "text-danger" : percentUsed >= 80 ? "text-warning" : "text-text-muted"
              } ${canSeeUsageDetail ? "hover:text-text-primary cursor-pointer" : "cursor-default"}`}
              title={canSeeUsageDetail ? "點擊查看 AI 用量明細" : undefined}
              aria-label={`本月 AI 用量 ${percentUsed}%`}
            >
              本月 {percentUsed}%
            </button>
          )}
          {capMonthTokens < 0 && (
            <span className="text-2xs text-text-muted ml-2">無上限</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Mobile-only IconRow (drawer view switcher). Desktop renders
              the same controls in the global Header next to the AI / 專業
              模式 toggle; here we surface them in the chat header so
              mobile doesn't get a separate strip above the panel. */}
          {variant === "inline" && aiConsoleSlot && (
            <div className="md:hidden">
              <AiConsoleIconRow
                view={aiConsoleSlot.view}
                onChange={aiConsoleSlot.setView}
                planSupported={aiConsoleSlot.planSupported}
                planPending={aiConsoleSlot.planPending}
              />
            </div>
          )}
          {/* Archive current conversation — shown only when a thread
              is active. Soft-deletes via DELETE /v1/ai/threads/:id
              (sets archived_at). Dispatch ai:works-changed so sidebar
              + ThreadStrip update immediately. */}
          {activeId && detail.kind === "ready" && (
            <button
              type="button"
              onClick={() => void deleteThread(activeId)}
              className="p-1.5 rounded hover:bg-bg-raised text-text-muted hover:text-text-primary"
              aria-label="封存目前對話"
              title="封存目前對話"
            >
              <Archive className="h-4 w-4" />
            </button>
          )}
          <LangSelect />
          {/* Close button hidden in inline mode — chat is always-on
              inside the parent page; no panel to "close". Spec §4.5. */}
          {variant !== "inline" && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-bg-raised text-text-muted hover:text-text-primary"
              aria-label="關閉 SENTRY 助手"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      {/* Thread list (compact horizontal scroller) — hidden in inline
          variant because the AI 模式 sidebar already shows the「工作」
          AI thread list with the same「新 AI 工作」 + select / delete
          actions. Showing both creates a duplicate strip the operator
          has to mentally reconcile. Spec docs/specs/ai-mode-operations-
          console.md §4.5. */}
      {variant !== "inline" && (
        <ThreadStrip
          threads={threads}
          activeId={activeId}
          onSelect={setActiveId}
          onNew={() => void newThread()}
          onDelete={(id) => void deleteThread(id)}
          creating={creating}
        />
      )}

      {/* Messages */}
      <div className="relative flex-1 min-h-0">
        <main ref={messagesScrollRef} className="absolute inset-0 overflow-y-auto p-4 space-y-3 bg-bg-base">
          <MessageList
            detail={detail}
            streaming={streaming}
            onStartTour={(p) => void handleStartTourInline(p)}
            onQuestionsSubmit={handleQuestionsSubmit}
            onQuestionsDismiss={handleQuestionsDismiss}
            dismissedQuestionStacks={dismissedQuestionStacks}
            localCards={Array.from(localCards.values()).filter(
              (c) => detail.kind === "ready" && c.thread_id === detail.thread.id,
            )}
          />
        </main>
        {/* Scroll-to-bottom button: appears when operator has scrolled
            up from the latest message. One tap returns to bottom. */}
        {showScrollDown && (
          <button
            type="button"
            onClick={scrollMessagesToBottom}
            aria-label="跳到最新訊息"
            title="跳到最新訊息"
            className="absolute bottom-3 right-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-bg-surface border border-border shadow-md text-text-secondary hover:text-text-primary hover:bg-bg-raised transition-colors"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Error banner */}
      {streamErr && (
        <div className="px-4 py-2 border-t border-danger/30 bg-danger/10 text-xs text-danger flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{streamErr}</span>
          <button onClick={() => setStreamErr(null)} className="text-text-muted hover:text-text-primary" aria-label="關閉錯誤訊息">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Composer */}
      {/* Tool execution status. Shown when AI is calling a tool — gives
          the user feedback during the multi-RTT hop. We translate tool
          names to user-friendly Chinese verbs so 「AI 正在查
          list_locations…」 doesn't leak internal identifiers. */}
      {activeToolName && (
        <div className="px-4 py-1.5 border-t border-border bg-bg-base text-2xs text-text-muted inline-flex items-center gap-1.5">
          <RefreshCw className="h-3 w-3 animate-spin" />
          <span>{toolActionLabelZh(activeToolName)}</span>
        </div>
      )}

      <footer className="border-t border-border bg-bg-surface p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={composing}
            onChange={(e) => setComposing(e.target.value)}
            onCompositionStart={() => { composingRef.current = true; }}
            onCompositionEnd={() => {
              // Safari / Firefox 把 compositionend 排在「終結選字那一下
              // Enter 的 keydown」之前；立刻把 ref 設回 false 會讓那次
              // keydown 看到 composingRef.current=false → 把選字 Enter
              // 當成送出而誤觸發。setTimeout 0 把清旗推到下個 microtask
              // (keydown 之後)，保留那一拍的「選字中」狀態。
              setTimeout(() => { composingRef.current = false; }, 0);
            }}
            onKeyDown={(e) => {
              // Desktop: Enter 送出、Shift+Enter 換行。
              // Touch device: Enter 換行；送出靠右邊箭頭（手機軟鍵盤沒
              // 方便的 Shift+Enter）。
              // IME 三層保險（任一為 true 就 ignore Enter）：
              //   1. composingRef.current     ← 自管 ref，蓋 Safari/Firefox 的 race
              //   2. e.nativeEvent.isComposing ← Chrome / Edge / 新 Safari 標準路徑
              //   3. e.keyCode === 229         ← 舊版 / Firefox legacy IME signal
              if (
                composingRef.current ||
                e.nativeEvent.isComposing ||
                e.keyCode === 229
              ) return;
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                !isTouchDevice
              ) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder={
              isGuest
                ? "尚未綁定組織，無法使用 AI 助手"
                : sending
                  ? "AI 回應中…"
                  : isTouchDevice
                    ? "問我系統怎麼用，或描述你想做的事…"
                    : "問我系統怎麼用，或描述你想做的事…（Enter 送出、Shift+Enter 換行）"
            }
            rows={2}
            disabled={isGuest || sending || quotaExhausted}
            className="flex-1 resize-none rounded-md bg-bg-base border border-border px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {sending ? (
            <button
              type="button"
              onClick={cancelStream}
              className="btn-secondary text-sm px-3 py-2"
              title="停止"
            >
              停止
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void send()}
              disabled={isGuest || !composing.trim() || quotaExhausted}
              className="btn-primary text-sm px-3 py-2 disabled:opacity-50"
              aria-label="送出"
            >
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
        {quotaExhausted && (
          <p className="mt-2 text-2xs text-warning">
            本月 AI 用量已達上限（{percentUsed}%）。下月 1 日重置；如需提早恢復請聯絡管理員。
            {canSeeUsageDetail && (
              <>
                {" "}
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/ai-usage")}
                  className="underline text-accent hover:text-accent-hover"
                >
                  查看用量明細
                </button>
              </>
            )}
          </p>
        )}
      </footer>

      {/* ── Tour overlay layer ───────────────────────────────────────────
       *   Both TourConfirmModal and TourEngine render via React Portal to
       *   document.body, so they visually escape this chat-window dialog
       *   and cover the whole dashboard. State lives in ChatWindow so the
       *   tool-result resume path (resumeWithToolResult) and the auto-
       *   refresh loadThreads() can reach it; collapsing the panel
       *   mid-tour cancels via the onClose path (see useEffect below).
       */}
      {pendingTour && (
        <TourConfirmModal
          proposal={pendingTour}
          onAccept={() => void handleTourAccept()}
          onReject={() => void handleTourReject()}
        />
      )}
      {activeTour && (
        <TourEngine
          proposal={activeTour}
          onComplete={(outcome, info) => void handleTourComplete(outcome, info)}
          onStep={(idx, outcome, reason) => {
            void logTourStep(activeTour.run_id, idx, outcome, reason).catch(() => {
              /* step logs are best-effort — failure shouldn't block the tour */
            });
          }}
        />
      )}
      {/* AI Plan & Apply modal — opens immediately on propose_plan SSE.
       *  Lives outside the tour layer because plan + tour are mutually
       *  exclusive (AI emits one OR the other; if both somehow arrive,
       *  the tour comes through pendingTour and plan through activePlan
       *  and they stack — both render, plan on top via higher z-index
       *  in PlanModal).
       *
       *  AM-5b: when `planHost === "external"` the host page (AI 導覽
       *  Topology) renders PlanPanel inside the drawer instead — we
       *  fire `onActivePlanChange` via the effect below and skip the
       *  floating modal render here. */}
      {activePlan && planHost === "modal" && (
        <PlanModal
          plan={activePlan.plan}
          tenantId={tenantParam}
          onClose={handlePlanClose}
        />
      )}
    </div>
  );
}

// ─── ThreadStrip ──────────────────────────────────────────────────────────

interface ThreadStripProps {
  threads: ThreadsState;
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  creating: boolean;
}

function ThreadStrip({ threads, activeId, onSelect, onNew, onDelete, creating }: ThreadStripProps): React.ReactElement {
  return (
    <div className="border-b border-border bg-bg-surface px-2 py-1.5 flex items-center gap-1 overflow-x-auto whitespace-nowrap">
      <button
        type="button"
        onClick={onNew}
        disabled={creating}
        className="inline-flex items-center gap-1 px-2 py-1 rounded text-2xs bg-accent/10 text-accent hover:bg-accent/20 disabled:opacity-50"
        title="開新對話"
      >
        <Plus className="h-3 w-3" /> 新對話
      </button>
      {threads.kind === "ready" && threads.items.length === 0 && (
        <span className="text-2xs text-text-muted px-2">尚無對話</span>
      )}
      {threads.kind === "ready" && threads.items.map((t) => (
        <div
          key={t.id}
          className={`inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded text-2xs border ${
            t.id === activeId
              ? "bg-accent/15 border-accent/40 text-text-primary"
              : "bg-bg-base border-border-subtle text-text-secondary hover:bg-bg-raised"
          }`}
        >
          <button
            type="button"
            onClick={() => onSelect(t.id)}
            className="max-w-[140px] truncate"
            title={t.title ?? "（未命名）"}
          >
            {t.title ?? "（未命名）"}
          </button>
          <button
            type="button"
            onClick={() => onDelete(t.id)}
            className="text-text-muted hover:text-danger p-0.5"
            aria-label={`封存 ${t.title ?? "對話"}`}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
      {threads.kind === "error" && (
        <span className="text-2xs text-danger px-2">{threads.message}</span>
      )}
    </div>
  );
}

// ─── MessageList ──────────────────────────────────────────────────────────

interface MessageListProps {
  detail: DetailState;
  streaming: StreamingAssistant | null;
  /** Inline 「開始引導」 button on each assistant bubble that carries an
   *  unresolved propose_tour calls this. ChatWindow wires it to the
   *  handleStartTourInline → POST /tours/:run_id/start path. */
  onStartTour?: (p: AiTourProposal & { thread_id: string; assistant_message_id: string }) => void;
  /** Operator submitted answers on a propose_questions stack. Posts
   *  a tool_result with the answer map so the chain resumes. */
  onQuestionsSubmit?: (call_id: string, answers: QuestionsAnswerMap) => void;
  /** Operator dismissed a propose_questions stack via「關閉」. The
   *  server-side orphan-resolver fills the sentinel on next message. */
  onQuestionsDismiss?: (call_id: string) => void;
  /** Volatile set of dismissed propose_questions call_ids — those
   *  cards render in「已關閉」 read-only mode. */
  dismissedQuestionStacks?: Set<string>;
  /** Non-persisted cards inserted at the end of the messages list.
   *  Created by handleDrawerFormComplete for credential-bearing
   *  outcomes (issue_binding_code) so the QR/code stays client-side. */
  localCards?: ReadonlyArray<LocalCard>;
}

function MessageList({ detail, streaming, onStartTour, onQuestionsSubmit, onQuestionsDismiss, dismissedQuestionStacks, localCards }: MessageListProps): React.ReactElement {
  // Auto-scroll on new content / stream tokens.
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [detail, streaming?.text]);

  if (detail.kind === "idle") {
    return (
      <div className="text-center text-text-muted text-xs py-8">
        <Sparkles className="h-6 w-6 mx-auto mb-2 text-accent/60" />
        <p className="font-medium text-text-primary">SENTRY 助手</p>
        <p className="mt-1">
          可以問我系統怎麼用、為什麼某個功能沒出現、要怎麼設定...
        </p>
      </div>
    );
  }
  if (detail.kind === "loading") {
    return <div className="text-center text-text-muted text-xs py-8">載入對話中…</div>;
  }
  if (detail.kind === "error") {
    return <div className="text-center text-danger text-xs py-8">{detail.message}</div>;
  }

  // Pre-compute which tool_call_ids have been resolved (have a matching
  // role='tool' message) — BUT skip the backend's auto-cancel sentinels
  // (`{cancelled:true, reason:"user_skipped"}`) written by the
  // resolveOrphanToolCalls self-heal on the next turn. That sentinel
  // exists so OpenAI's chain validation passes; it does NOT mean the
  // user actually saw / dismissed the proposal. If we treated it as
  // resolved, the inline button would vanish before the user ever had
  // a chance to click it — exactly the bug the user kept hitting:
  // 「依然沒有進入引導模式」.
  const resolvedCallIds = new Set<string>();
  for (const m of detail.messages) {
    if (m.role !== "tool" || !m.tool_call_id) continue;
    // Filter the auto-cancel sentinel out so the propose_tour stays
    // surfaceable. Real user-driven tool_results (accepted:true /
    // accepted:false from the tour engine, or output from list_X tool
    // executors) don't carry this exact payload.
    try {
      const parsed = JSON.parse(m.content) as { cancelled?: unknown; reason?: unknown };
      if (parsed && parsed.cancelled === true && parsed.reason === "user_skipped") continue;
    } catch { /* content isn't JSON — definitely a real tool result */ }
    resolvedCallIds.add(m.tool_call_id);
  }

  // Interleave persisted messages with LocalCards in chronological order
  // (by created_at). The streaming bubble + endRef ALWAYS sit last so the
  // in-flight assistant token stream stays visually anchored to the
  // bottom. Prior to this fix LocalCards were always pushed below every
  // message, so the QR / 邀請碼 card appeared *after* subsequent dialogue
  // turns — confusing & unhelpful in flows where the operator continues
  // chatting after issuing a code.
  type TimelineItem =
    | { kind: "message"; created_at: string; node: React.ReactNode }
    | { kind: "card"; created_at: string; node: React.ReactNode };
  const timeline: TimelineItem[] = [
    ...detail.messages.map((m): TimelineItem => ({
      kind: "message",
      created_at: m.created_at,
      node: (
        <MessageBubble
          key={`msg-${m.id}`}
          message={m}
          resolvedCallIds={resolvedCallIds}
          onStartTour={onStartTour}
          onQuestionsSubmit={onQuestionsSubmit}
          onQuestionsDismiss={onQuestionsDismiss}
          dismissedQuestionStacks={dismissedQuestionStacks}
        />
      ),
    })),
    ...(localCards ?? []).map((card, i): TimelineItem => ({
      kind: "card",
      created_at: card.created_at,
      node: <LocalCardBubble key={`local-${i}-${card.kind}-${card.created_at}`} card={card} />,
    })),
  ];
  timeline.sort((a, b) => a.created_at.localeCompare(b.created_at));

  return (
    <>
      {timeline.length === 0 && !streaming && (
        <div className="text-center text-text-muted text-xs py-8">
          這是一個空對話。在下方輸入訊息開始。
        </div>
      )}
      {timeline.map((t) => t.node)}
      {streaming && (
        <MessageBubble
          streaming
          streamingText={streaming.text}
          streamingQuickNav={streaming.quick_nav ?? null}
          streamingQuestions={streaming.questionStacks ?? null}
          onQuestionsSubmit={onQuestionsSubmit}
          onQuestionsDismiss={onQuestionsDismiss}
          dismissedQuestionStacks={dismissedQuestionStacks}
        />
      )}
      <div ref={endRef} />
    </>
  );
}

function LocalCardBubble({ card }: { card: LocalCard }): React.ReactElement {
  if (card.kind === "issued_binding_code") {
    return (
      <div className="flex items-start gap-2">
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-2xs text-text-muted mb-1.5">邀請碼已產生</div>
          <BindingCodeIssuedCard issued={card.issued} />
        </div>
      </div>
    );
  }
  return <></>;
}

type MessageBubbleProps =
  | {
      message: AiMessageRow;
      streaming?: false;
      streamingText?: undefined;
      streamingQuickNav?: undefined;
      streamingQuestions?: undefined;
      resolvedCallIds?: Set<string>;
      onStartTour?: (p: AiTourProposal & { thread_id: string; assistant_message_id: string }) => void;
      onQuestionsSubmit?: (call_id: string, answers: QuestionsAnswerMap) => void;
      onQuestionsDismiss?: (call_id: string) => void;
      dismissedQuestionStacks?: Set<string>;
    }
  | {
      message?: undefined;
      streaming: true;
      streamingText: string;
      /** Quick-nav items live-emitted via SSE during this stream.
       *  Post-stream the same items come from tool_calls JSON. */
      streamingQuickNav?: QuickNavItem[] | null;
      /** propose_questions stacks live-emitted via SSE during this stream. */
      streamingQuestions?: QuestionsProposal[] | null;
      resolvedCallIds?: undefined;
      onStartTour?: undefined;
      onQuestionsSubmit?: (call_id: string, answers: QuestionsAnswerMap) => void;
      onQuestionsDismiss?: (call_id: string) => void;
      dismissedQuestionStacks?: Set<string>;
    };

function MessageBubble(props: MessageBubbleProps): React.ReactElement | null {
  const { streaming, streamingText, streamingQuickNav, streamingQuestions, message, resolvedCallIds, onStartTour, onQuestionsSubmit, onQuestionsDismiss, dismissedQuestionStacks } = props;
  const role = streaming ? "assistant" : (message as AiMessageRow).role;
  const content = streaming ? (streamingText ?? "") : (message as AiMessageRow).content;
  const messageId = streaming ? undefined : (message as AiMessageRow).id;
  const toolCallsJson = streaming ? null : (message as AiMessageRow).tool_calls;

  if (role === "system" || role === "tool") return null;
  const isUser = role === "user";

  // Detect unresolved propose_tour on this assistant message → render
  // an inline button so the user can always reach the tour even if the
  // modal auto-popup failed. Safe to compute every render — set lookups
  // are O(1) and propose_tour args are small.
  // Parse tool_calls JSON once. Used for both the inline propose_tour
  // button AND the diagnostic chips that surface "AI is querying X"
  // for intermediate tool-call turns (so the bubble is never silently
  // empty).
  let inlineProposal:
    | { call_id: string; title: string; summary: string; steps: AiTourStep[]; emptySteps: boolean }
    | null = null;
  let toolCallDigest: Array<{ call_id: string; name: string; isPropTour: boolean }> = [];
  // Quick-nav items: surfaced during streaming via streaming.quick_nav,
  // and post-stream from tool_calls JSON. Two sources, same render.
  let inlineQuickNavItems: QuickNavItem[] | null = streamingQuickNav ?? null;
  // propose_questions stacks: live during streaming, post-stream from
  // tool_calls JSON. Card with its call_id in resolvedCallIds renders
  // as「已回覆」 instead of an interactive picker.
  let inlineQuestionStacks: QuestionsProposal[] = streamingQuestions ?? [];
  if (!isUser && !streaming && toolCallsJson) {
    try {
      const calls = JSON.parse(toolCallsJson) as Array<{
        call_id: string; name: string; args?: Record<string, unknown>;
      }>;
      toolCallDigest = calls
        .filter((c) => c.call_id && c.name)
        .map((c) => ({ call_id: c.call_id, name: c.name, isPropTour: c.name === "propose_tour" }));
      if (resolvedCallIds && onStartTour) {
        for (const c of calls) {
          if (c.name !== "propose_tour") continue;
          if (resolvedCallIds.has(c.call_id)) continue;
          const args = c.args ?? {};
          const steps = Array.isArray((args as { steps?: unknown }).steps)
            ? (args as { steps: AiTourStep[] }).steps
            : [];
          // KEEP empty-step proposals — render the inline button anyway
          // with an annotation. Filtering them out left the user staring
          // at a silent bubble when the AI hallucinated a malformed
          // propose_tour. Better to show "（步驟尚未產生，請重新詢問）"
          // than to render nothing.
          inlineProposal = {
            call_id: c.call_id,
            title: typeof (args as { title?: unknown }).title === "string"
              ? (args as { title: string }).title : "",
            summary: typeof (args as { summary?: unknown }).summary === "string"
              ? (args as { summary: string }).summary : "",
            steps,
            emptySteps: steps.length === 0,
          };
          break; // only one propose_tour per assistant message
        }
      }
      // Quick-nav extraction. AI may emit multiple propose_quick_nav
      // calls per assistant message; concat all items, then cap at 3
      // (mirrors the server-side cap so a malformed payload can't blow
      // up the bubble layout).
      for (const c of calls) {
        if (c.name !== "propose_quick_nav") continue;
        const args = c.args ?? {};
        const items = Array.isArray((args as { items?: unknown }).items)
          ? ((args as { items: unknown[] }).items as QuickNavItem[])
          : [];
        if (items.length === 0) continue;
        inlineQuickNavItems = (inlineQuickNavItems ?? []).concat(items).slice(0, 3);
      }
      // propose_questions extraction — multi-card picker stacks.
      // Validation mirrors the SSE-time sanitizer in chatApi.ts so a
      // chat-history re-render gets the same shape as the live stream.
      for (const c of calls) {
        if (c.name !== "propose_questions") continue;
        const args = c.args ?? {};
        const rawQuestions = Array.isArray((args as { questions?: unknown }).questions)
          ? ((args as { questions: unknown[] }).questions) : [];
        const questions = [] as QuestionsProposal["questions"];
        for (const rawQ of rawQuestions) {
          if (!rawQ || typeof rawQ !== "object") continue;
          const q = rawQ as {
            question?: unknown; header?: unknown; multiSelect?: unknown;
            allow_other?: unknown; reason?: unknown; options?: unknown;
          };
          if (typeof q.question !== "string" || q.question.length === 0) continue;
          const header = typeof q.header === "string" ? q.header : "";
          if (header.length === 0) continue;
          const rawOpts = Array.isArray(q.options) ? q.options : [];
          const opts = [] as QuestionsProposal["questions"][number]["options"];
          for (const r of rawOpts) {
            if (!r || typeof r !== "object") continue;
            const o = r as { value?: unknown; label?: unknown; description?: unknown; icon?: unknown };
            if (typeof o.value !== "string" || typeof o.label !== "string") continue;
            const opt: typeof opts[number] = { value: o.value, label: o.label };
            if (typeof o.description === "string" && o.description.length > 0) opt.description = o.description;
            if (typeof o.icon === "string" && o.icon.length > 0) opt.icon = o.icon;
            opts.push(opt);
          }
          if (opts.length < 2) continue;
          questions.push({
            question: q.question,
            header,
            multiSelect: q.multiSelect === true,
            allow_other: q.allow_other !== false,
            reason: typeof q.reason === "string" ? q.reason : "",
            options: opts,
          });
        }
        if (questions.length === 0) continue;
        inlineQuestionStacks = inlineQuestionStacks.concat({
          call_id: c.call_id,
          questions,
        });
      }
    } catch { /* tolerate */ }
  }
  return (
    <div className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed break-words ${
          // User bubbles render raw text (preserve literal input
          // including stray `**` etc.); assistant bubbles render
          // Markdown so tables / lists / code blocks display
          // properly. whitespace-pre-wrap only on user bubbles —
          // ChatMarkdown handles its own whitespace.
          isUser
            ? "bg-accent text-bg-base whitespace-pre-wrap"
            : "bg-bg-surface border border-border text-text-primary"
        }`}
      >
        {/* Streaming placeholder: three bouncing dots until the first
         *  token arrives. Once content starts streaming, switch to the
         *  in-line caret animation (already present pattern). */}
        {streaming && !content && (
          <span aria-label="AI 正在思考" className="inline-flex items-center text-text-secondary py-0.5">
            <span className="ai-thinking-dot" />
            <span className="ai-thinking-dot" />
            <span className="ai-thinking-dot" />
          </span>
        )}
        {/* Bubble-text fallback: AI sometimes emits ONLY propose_tour
         *  with no accompanying assistant text. The bubble then looks
         *  visually empty — confusing. When content is empty AND we
         *  have an inline proposal to surface, synthesize a friendly
         *  one-liner inside the bubble so the user sees clearly that
         *  the AI prepared a guide. */}
        {!streaming && !content && inlineProposal && (
          <span className="text-text-secondary">
            {inlineProposal.emptySteps
              ? <>⚠️ AI 想建議引導「{inlineProposal.title || "未命名"}」但步驟資料缺失。請更具體描述要做什麼。</>
              : <>💡 我為你準備了一個逐步引導：<span className="text-text-primary font-medium">{inlineProposal.title || "未命名"}</span>，點下方按鈕開始。</>}
          </span>
        )}
        {/* Diagnostic for empty-content assistant bubbles that have
         *  tool_calls but NO inline proposal — i.e. AI is in the middle
         *  of an intermediate tool round-trip (list_sites, etc.) and
         *  didn't write any text this turn. Surface what's happening
         *  so the user isn't staring at a silent black oval. */}
        {!streaming && !content && !inlineProposal && toolCallDigest.length > 0 && (
          <span className="text-text-secondary text-2xs inline-flex flex-wrap gap-1">
            <span className="text-text-muted">AI 已執行：</span>
            {toolCallDigest.map((tc) => (
              <span key={tc.call_id} className="px-1.5 py-0.5 rounded bg-bg-raised border border-border font-mono">
                {toolActionLabelZh(tc.name).replace(/^AI 正在/, "").replace(/…$/, "") || tc.name}
              </span>
            ))}
          </span>
        )}
        {/* Last-resort fallback: truly empty bubble (no content, no
         *  inline proposal, no tool_calls). Better than a silent oval. */}
        {!streaming && !content && !inlineProposal && toolCallDigest.length === 0 && (
          <span className="text-text-muted text-2xs italic">
            （AI 沒有產生可顯示的訊息，請再試一次）
          </span>
        )}
        {content && (isUser ? content : <ChatMarkdown content={content} />)}
        {streaming && content && <span className="inline-block ml-1 w-1.5 h-3 bg-current opacity-60 animate-pulse rounded-sm" aria-hidden="true" />}
      </div>
      {/* Inline 「點開引導」 CTA — assistant message has a propose_tour
       *  in its tool_calls JSON that no role='tool' message has
       *  resolved. This is the fallback UI when the auto-popup modal
       *  didn't fire (SSE event dropped, race condition, etc.). The
       *  user always has a clear thing to click. */}
      {inlineProposal && messageId && onStartTour && !inlineProposal.emptySteps && (
        <button
          type="button"
          onClick={() => {
            const runId = "recover:" + (typeof crypto !== "undefined" && "randomUUID" in crypto
              ? (crypto as { randomUUID: () => string }).randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
            onStartTour({
              call_id: inlineProposal.call_id,
              run_id: runId,
              title: inlineProposal.title,
              summary: inlineProposal.summary,
              steps: inlineProposal.steps,
              thread_id: "", // ChatWindow rewrites this from activeId
              assistant_message_id: messageId,
            });
          }}
          className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-accent/50 bg-accent/10 px-2.5 py-1.5 text-xs text-accent hover:bg-accent/20 transition-colors"
        >
          <Sparkles className="h-3.5 w-3.5" />
          <span>點開引導：{inlineProposal.title || "未命名"} <span className="text-text-muted">({inlineProposal.steps.length} 步)</span></span>
        </button>
      )}
      {inlineQuickNavItems && inlineQuickNavItems.length > 0 && (
        <QuickNavRow items={inlineQuickNavItems} />
      )}
      {inlineQuestionStacks.length > 0 && (
        <div className="mt-2 flex flex-col gap-2 w-full max-w-[85%]">
          {inlineQuestionStacks.map((stack) => (
            <PropQuestionsCard
              key={stack.call_id}
              proposal={stack}
              resolved={resolvedCallIds?.has(stack.call_id) ?? false}
              dismissed={dismissedQuestionStacks?.has(stack.call_id) ?? false}
              onSubmit={(callId, answers) => onQuestionsSubmit?.(callId, answers)}
              onDismiss={(callId) => onQuestionsDismiss?.(callId)}
            />
          ))}
        </div>
      )}
      {/* Thumbs UI only on assistant messages with a persisted id — skip
       *  the streaming placeholder, and skip user messages. PR #8. */}
      {!isUser && !streaming && messageId && content && (
        <FeedbackThumbs messageId={messageId} />
      )}
    </div>
  );
}

type IconComp = React.ComponentType<{ className?: string }>;
const QUICK_NAV_ICONS: Record<string, IconComp> = {
  page: ArrowRight as IconComp,
  settings: Settings as IconComp,
  person: User as IconComp,
  shield: Shield as IconComp,
  plus: FileText as IconComp,
  info: Info as IconComp,
};
/** Render inline quick-nav buttons on an assistant message. Each button
 *  navigates to a registered dashboard route on click, and optionally
 *  programmatically clicks a `data-tour-id` element after the route
 *  mounts (used to auto-open drawers — e.g. land on /dashboard/
 *  locations then click `locations.create.button` to open the
 *  New-Location drawer). Client-side RBAC filter (defence-in-depth;
 *  server already filters in threads.ts) so officers don't see a
 *  「前往平台設定」 button slip through. */
function QuickNavRow({ items }: { items: QuickNavItem[] }): React.ReactElement | null {
  const navigate = useNavigate();
  const session = useDashboardSession();
  // AI 模式現在是純 overlay：sidebar 跟 chat 在任何頁面都跟著操作員，
  // 點 quick_nav 不再要求切換到專業模式 — AppLayout 會在非 /topology
  // 頁面 render 浮動 ChatBubble 維持 chat 存取性。
  // Permission-based RBAC filter (Custom RBAC §6) — drop items the caller
  // can't reach. super_admin god-mode bypass via the actor permissions
  // already containing every permission.
  const isSuperAdmin = session.super_admin === true && !session.acting_tenant_id;
  const have = new Set(session.permissions);
  const allowed = items.filter((it) => {
    if (isSuperAdmin) return true;
    const required = ROUTE_PERMISSIONS[it.route];
    if (!required) return false;          // unknown route — block
    if (required.length === 0) return true;  // empty list — any authed
    return required.some((p) => have.has(p));
  });
  if (allowed.length === 0) return null;

  const performNavigation = (item: QuickNavItem) => {
    // On MOBILE the chat panel covers ~95% of the viewport — clicking
    // an inline nav button without animating the panel away would jump
    // the operator to the destination page that they can't see (still
    // covered by chat). Dispatch a close-animated event; ChatBubble
    // listens and slides the panel out (then unmounts) before the
    // route swap settles. On DESKTOP the chat is a 400×720 corner
    // widget, the destination page is already visible behind it, so
    // we leave the chat open.
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches) {
      window.dispatchEvent(new CustomEvent("ai-chat:close-animated"));
    }
    navigate(item.route);
    if (!item.then_click) return;
    // Poll for the target data-tour-id to mount + become visible, then
    // programmatically click. 3s budget so a slow page-load doesn't
    // hang. If the element never appears we silently no-op — UX-wise
    // the user is already on the right page, which is half the win.
    const tourId = item.then_click;
    const start = Date.now();
    const tick = () => {
      if (Date.now() - start > 3000) return;
      const matches = document.querySelectorAll<HTMLElement>(`[data-tour-id="${CSS.escape(tourId)}"]`);
      for (const el of matches) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0 && r.right > 0 && r.bottom > 0
            && r.left < window.innerWidth && r.top < window.innerHeight) {
          el.click();
          return;
        }
      }
      setTimeout(tick, 150);
    };
    setTimeout(tick, 100); // small initial delay so the route's mount effects can run
  };

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {allowed.map((item, i) => {
        const Icon: IconComp = (item.icon && QUICK_NAV_ICONS[item.icon]) || (ArrowRight as IconComp);
        return (
          <button
            key={`${item.route}:${i}`}
            type="button"
            onClick={() => performNavigation(item)}
            className="inline-flex items-center gap-1.5 rounded-md border border-accent/50 bg-accent/10 px-2.5 py-1.5 text-xs text-accent hover:bg-accent/20 transition-colors"
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function FeedbackThumbs({ messageId }: { messageId: string }): React.ReactElement {
  const [sent, setSent] = useState<"up" | "down" | null>(null);
  const [busy, setBusy] = useState<"up" | "down" | null>(null);
  const submit = async (sentiment: "up" | "down"): Promise<void> => {
    if (busy || sent === sentiment) return;
    setBusy(sentiment);
    try {
      await sendAiFeedback(messageId, sentiment);
      setSent(sentiment);
    } catch { /* tolerate — bubble silently */ } finally {
      setBusy(null);
    }
  };
  return (
    <div className="mt-1 flex items-center gap-1 text-text-muted">
      <button
        type="button"
        onClick={() => void submit("up")}
        className={`p-1 rounded hover:bg-bg-raised transition-colors ${sent === "up" ? "text-success" : ""}`}
        aria-label="這個回答有幫助"
        title="有幫助"
        disabled={!!busy}
      >
        <ThumbsUp className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={() => void submit("down")}
        className={`p-1 rounded hover:bg-bg-raised transition-colors ${sent === "down" ? "text-danger" : ""}`}
        aria-label="這個回答沒幫助"
        title="沒幫助"
        disabled={!!busy}
      >
        <ThumbsDown className="h-3 w-3" />
      </button>
    </div>
  );
}

// ─── LangSelect ───────────────────────────────────────────────────────────
// Inline header dropdown for AI 回覆語言。"auto" = AI 自行偵測使用者
// 訊息語言；其他選項強制固定。寫入後 ChatWindow 不需重整 — 下一輪
// 訊息會帶上新 lang 進 system prompt。

// ─── Tool action labels ───────────────────────────────────────────────────
// User-facing zh-TW phrases for the activeToolName status bar. The raw
// tool identifiers (list_sites / propose_tour / inspect_tenant_state)
// leak internal naming if shown directly. Fallback for unknown names is
// the generic "處理中…" so future tools don't surface as raw strings.
const TOOL_ACTION_LABEL_ZH: Record<string, string> = {
  get_me: "AI 正在確認您的身分…",
  list_sites: "AI 正在查案場列表…",
  list_locations: "AI 正在查地點…",
  list_routes: "AI 正在查路線…",
  list_tasks: "AI 正在查任務…",
  list_users: "AI 正在查使用者…",
  list_clients: "AI 正在查客戶列表…",
  list_incidents: "AI 正在查事件…",
  list_task_assignments: "AI 正在查任務派發…",
  list_officers: "AI 正在查巡邏員…",
  list_shifts: "AI 正在查班別…",
  list_schedule_roster: "AI 正在查班表…",
  list_active_patrol_sessions: "AI 正在查在勤狀態…",
  list_notification_logs: "AI 正在查通知紀錄…",
  list_reports: "AI 正在查報表…",
  list_patrol_events: "AI 正在查巡邏紀錄…",
  get_officer_enrollment: "AI 正在查巡邏員註冊狀態…",
  get_today_overview: "AI 正在彙整今日概況…",
  inspect_tenant_state: "AI 正在彙整單位狀態…",
  inspect_tenant_topology: "AI 正在彙整結構…",
  inspect_current_page: "AI 正在分析目前畫面…",
  inspect_scan_session: "AI 正在查感應紀錄…",
  summarize_patrol_activity: "AI 正在彙整巡邏活動…",
  // UI directives — propose_* emit their own SSE event but the
  // preceding tool_call_started still fires + sets activeToolName;
  // without an entry here the spinner falls back to「AI 處理中…」
  // and the operator can't tell which directive AI just decided on.
  propose_tour: "AI 正在準備引導步驟…",
  propose_quick_nav: "AI 正在準備捷徑…",
  propose_plan: "AI 正在準備計畫…",
  propose_focus_topology: "AI 正在準備聚焦結構…",
  propose_drawer_form: "AI 正在準備新增表單…",
  propose_questions: "AI 正在準備問題…",
  suggest_shared_skill: "AI 正在整理共用知識…",
  // super_admin-only
  list_tenants: "AI 正在查所有客戶單位…",
  inspect_platform_state: "AI 正在彙整平台狀態…",
  inspect_tenant_state_for: "AI 正在查指定單位狀態…",
};
function toolActionLabelZh(name: string): string {
  return TOOL_ACTION_LABEL_ZH[name] ?? "AI 處理中…";
}

// ─── Orphan propose_tour recovery ─────────────────────────────────────
// When the SSE stream breaks between `tool_call_started` and
// `tool_call_complete` (Cloudflare proxy idle timeout, network drop),
// the propose_tour event never reaches the browser. The proposal,
// however, is still persisted server-side once the stream's try/catch
// runs through to the UPDATE batch — it lands in
// `user_ai_messages.tool_calls` as a JSON array containing
// `{call_id, name: "propose_tour", args: { title, summary, steps[] }}`.
//
// On thread load (initial fetch or refresh after error), scan the
// messages newest-first. If the latest assistant message carries a
// propose_tour entry AND no later role='tool' message resolved that
// call_id, surface it as pendingTour so the modal appears anyway.
function findOrphanProposal(messages: AiMessageRow[]): {
  call_id: string;
  run_id: string;
  title: string;
  summary: string;
  steps: AiTourStep[];
  assistant_message_id: string;
} | null {
  // Index tool_call_id → resolved? (any role='tool' message with that id).
  const resolved = new Set<string>();
  for (const m of messages) {
    if (m.role === "tool" && m.tool_call_id) resolved.add(m.tool_call_id);
  }
  // Walk newest-first to find the most recent unresolved propose_tour.
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== "assistant" || !m.tool_calls) continue;
    let calls: Array<{ call_id: string; name: string; args?: Record<string, unknown> }>;
    try {
      calls = JSON.parse(m.tool_calls);
    } catch { continue; }
    if (!Array.isArray(calls)) continue;
    for (const c of calls) {
      if (c.name !== "propose_tour" || !c.call_id) continue;
      if (resolved.has(c.call_id)) continue;
      const args = c.args ?? {};
      const steps = Array.isArray((args as { steps?: unknown }).steps)
        ? ((args as { steps: AiTourStep[] }).steps)
        : [];
      if (steps.length === 0) continue; // propose_tour with no steps is unusable
      // Recovered proposals don't have a server-generated run_id (that's
      // emitted only via the live SSE), so we mint a client-side one
      // prefixed `recover:` — POST /v1/ai/tours/:run_id/start accepts
      // any unique id and writes it as the row PK. Audit treats the
      // recover prefix as the trace of "resumed-after-disconnect".
      const runId = "recover:" + (typeof crypto !== "undefined" && "randomUUID" in crypto
        ? (crypto as { randomUUID: () => string }).randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
      return {
        call_id: c.call_id,
        run_id: runId,
        title: typeof (args as { title?: unknown }).title === "string" ? (args as { title: string }).title : "",
        summary: typeof (args as { summary?: unknown }).summary === "string" ? (args as { summary: string }).summary : "",
        steps,
        assistant_message_id: m.id,
      };
    }
  }
  return null;
}

const LANG_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "auto", label: "自動" },
  { value: "zh-TW", label: "繁中" },
  { value: "en", label: "EN" },
  { value: "ja", label: "日" },
  { value: "zh-CN", label: "简中" },
];

function LangSelect(): React.ReactElement {
  const [value, setValue] = useState<string>("auto");
  const [busy, setBusy] = useState(false);

  // Hydrate from server on mount. Silently fall back to 'auto' if the
  // read fails — language pref isn't critical to chat function.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const prefs = await getAiPreferences();
        if (!cancelled && typeof prefs.lang === "string") setValue(prefs.lang);
      } catch { /* tolerate */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const onChange = async (next: string) => {
    setValue(next);
    setBusy(true);
    try { await setAiPreference("lang", next); }
    catch { /* tolerate — next render still shows new value optimistically */ }
    finally { setBusy(false); }
  };

  return (
    <select
      value={value}
      onChange={(e) => void onChange(e.target.value)}
      disabled={busy}
      title="AI 回覆語言"
      aria-label="AI 回覆語言"
      className="bg-bg-surface border border-border rounded text-2xs px-1 py-0.5 text-text-secondary hover:text-text-primary focus:outline-none focus:border-accent"
    >
      {LANG_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
